// Central chart palettes + ECharts option builder for all widget types.
import {
  seriesColorOf, gradientFill, hexA, markerConfig, catAxisRotation, valueAxisBounds,
  cartesianLabelFormatter, pieLabelFormatter, funnelLabelFormatter, applyDataZoom, dataLabelStyle, dataLabelStyleFrom,
} from "@/lib/chartStyle";
import { detectValueKind, smartComparator } from "@/lib/filterValues";
import { resolveRegionName as resolveRegion } from "@/lib/geo";

export const PALETTES = {
  "Eazy Core": ["#2B4EFF", "#FF6B35", "#00C2A8", "#FFB020", "#8B5CF6", "#EC4899", "#0EA5E9", "#64748B", "#22C55E", "#94A3B8"],
  "Categorical Soft": ["#5B78FF", "#FF8A5C", "#2DD9BE", "#FFC24D", "#A78BFA", "#F472B6", "#38BDF8", "#86EFAC"],
  "Ocean": ["#2B4EFF", "#0EA5E9", "#00C2A8", "#22D3EE", "#5B78FF", "#14B8A6", "#7DB7FF", "#5EEAD4", "#60A5FA", "#67E8F9"],
  "Sunset": ["#FF6B35", "#FF8FA3", "#FFB020", "#FFD166", "#F4978E", "#F8AD9D", "#FBC4AB", "#FF9E80", "#FFCC80", "#FFADAD"],
  "Forest": ["#00C2A8", "#3DD68C", "#84CC16", "#22C55E", "#10B981", "#65A30D", "#A3E635", "#4ADE80", "#16A34A", "#BEF264"],
  "Berry": ["#8B5CF6", "#F0A6D8", "#EC4899", "#C084FC", "#E879F9", "#F472B6", "#D946EF", "#9D7BFF", "#FF8FB1", "#E9A8FF"],
  "Corporate": ["#2B4EFF", "#0A1029", "#64748B", "#94A3B8", "#0EA5E9", "#475569", "#7DB7FF", "#1E293B", "#38BDF8", "#CBD5E1"],
  // Back-compat: keep old default key so previously-saved widgets still resolve a palette
  "Vivid Pastel": ["#2B4EFF", "#FF6B35", "#00C2A8", "#FFB020", "#5B78FF", "#EC4899", "#2DD4BF", "#60A5FA", "#F0A6D8", "#94A3B8"],
};

export const PALETTE = PALETTES["Eazy Core"];

export const SEQUENTIAL_OCEAN = ["#E8EDFF", "#C2CFFF", "#8AA2FF", "#4E6DFF", "#2B4EFF"];

const FONT = "Manrope";
const DISPLAY_FONT = "Outfit";

const isDark = () => typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
const INK = () => (isDark() ? "#EDF1FB" : "#0A1029");
const INK2 = () => (isDark() ? "rgba(214,224,255,0.85)" : "#232B4A");
const INK3 = () => (isDark() ? "rgba(160,175,215,0.9)" : "#5A6284");
const CARD_BG = () => (isDark() ? "#101527" : "#fff");
const SEQ_LOW = () => (isDark() ? "#16224A" : "#E8EDFF");
const SEQ_HIGH = () => (isDark() ? "#B9C8FF" : "#101E66");

const AXIS_LABEL = { color: "rgba(35,43,74,0.78)", fontFamily: FONT };
const AXIS_LINE = { lineStyle: { color: "rgba(10,16,41,0.22)" } };
const SPLIT_LINE = { lineStyle: { color: "rgba(10,16,41,0.08)" } };

const baseTooltip = {
  backgroundColor: "rgba(255,255,255,0.92)",
  borderColor: "rgba(43,78,255,0.22)",
  borderWidth: 1,
  textStyle: { color: "#0A1029", fontFamily: FONT },
  extraCssText: "box-shadow: 0 18px 60px rgba(10,16,41,0.18); border-radius: 14px; backdrop-filter: blur(6px);",
};

const baseLegend = {
  textStyle: { color: "rgba(35,43,74,0.9)", fontFamily: FONT },
  icon: "roundRect", itemWidth: 12, itemHeight: 12, bottom: 0, type: "scroll",
};

export function syncChartTheme() {
  const d = isDark();
  AXIS_LABEL.color = d ? "rgba(214,224,255,0.72)" : "rgba(35,43,74,0.78)";
  AXIS_LINE.lineStyle.color = d ? "rgba(198,212,255,0.28)" : "rgba(10,16,41,0.22)";
  SPLIT_LINE.lineStyle.color = d ? "rgba(198,212,255,0.10)" : "rgba(10,16,41,0.08)";
  baseTooltip.backgroundColor = d ? "rgba(16,21,39,0.94)" : "rgba(255,255,255,0.92)";
  baseTooltip.borderColor = d ? "rgba(108,135,255,0.32)" : "rgba(43,78,255,0.22)";
  baseTooltip.textStyle.color = INK();
  baseLegend.textStyle.color = d ? "rgba(214,224,255,0.9)" : "rgba(35,43,74,0.9)";
}

// Dashboard-level chart font theme (backlog add-on B): one font for every chart's
// axis labels, titles, tooltips and legends.
let CHART_FONT = FONT;
export function setChartFont(f) {
  CHART_FONT = (f && String(f).split(",")[0].trim()) || FONT;
  AXIS_LABEL.fontFamily = CHART_FONT;
  baseTooltip.textStyle.fontFamily = CHART_FONT;
  baseLegend.textStyle.fontFamily = CHART_FONT;
}

// ---- Items 34/36/38/44/49: independent per-axis formatting ----
function axisFont(f = {}) {
  return {
    ...(f.color ? { color: f.color } : {}),
    ...(f.family ? { fontFamily: String(f.family).split(",")[0].trim() } : { fontFamily: CHART_FONT }),
    ...(f.size ? { fontSize: Math.max(7, Math.min(32, Number(f.size))) } : {}),
    ...(f.bold ? { fontWeight: "bold" } : {}),
    ...(f.italic ? { fontStyle: "italic" } : {}),
  };
}

// Mutates an ECharts axis object with an axisFmt spec { title, labels, line, grid }.
export function applyAxisFmt(axis, fmt) {
  if (!axis || !fmt) return axis;
  const t = fmt.title || {};
  const l = fmt.labels || {};
  if (t.show === false) { axis.name = ""; }
  else if (t.text) axis.name = t.text;
  axis.nameTextStyle = { ...(axis.nameTextStyle || {}), ...axisFont(t) };
  if (t.padding) axis.nameGap = Number(axis.nameGap || 30) + Number(t.padding);
  axis.axisLabel = { ...(axis.axisLabel || {}), ...axisFont(l) };
  if (l.show === false) axis.axisLabel.show = false;
  if (l.rotation !== undefined && l.rotation !== "" && l.rotation !== "auto") axis.axisLabel.rotate = Number(l.rotation) || 0;
  if (l.wrapMode === "wrap") { axis.axisLabel.overflow = "break"; axis.axisLabel.width = Number(l.maxWidth) || 84; axis.axisLabel.interval = 0; }
  else if (l.wrapMode === "trim") { axis.axisLabel.overflow = "truncate"; axis.axisLabel.width = Number(l.maxWidth) || 84; }
  if (l.padding) axis.axisLabel.margin = 8 + Number(l.padding);
  const ln = fmt.line || {};
  if (ln.show === false) axis.axisLine = { show: false };
  else if (ln.color || ln.width) {
    axis.axisLine = { ...(axis.axisLine || {}), show: true, lineStyle: { ...((axis.axisLine || {}).lineStyle || {}), ...(ln.color ? { color: ln.color } : {}), ...(ln.width ? { width: Number(ln.width) } : {}) } };
  }
  const g = fmt.grid || {};
  if (g.show === false) axis.splitLine = { show: false };
  else if (g.color || g.width || g.style) {
    axis.splitLine = { show: true, lineStyle: { ...((axis.splitLine || {}).lineStyle || {}), ...(g.color ? { color: g.color } : {}), ...(g.width ? { width: Number(g.width) } : {}), ...(g.style ? { type: g.style } : {}) } };
  }
  return axis;
}

const baseGrid = { left: 8, right: 16, top: 24, bottom: 36, containLabel: true };

export const DEFAULT_OPTIONS = {
  palette: "Eazy Core",
  colorByCategory: true,
  gradient: true,
  showLegend: true,
  showGrid: true,
  showLabels: false,
  smooth: true,
};

function num(v) { const n = Number(v); return isNaN(n) ? 0 : n; }
// Item 8: line charts keep missing points as gaps instead of fabricating zeros.
function numOrNull(v) { if (v === null || v === undefined || v === "") return null; const n = Number(v); return isNaN(n) ? null : n; }

function themePalette() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--chart-colors").trim();
    if (v) return v.split(",").map((s) => s.trim()).filter(Boolean);
  } catch (e) {
    console.debug("chartTheme: --chart-colors unavailable, using static palette", e);
  }
  return null;
}

function getColors(options) {
  if (options.colors && options.colors.length) return options.colors;
  if (!options.palette || options.palette === "Eazy Core" || options.palette === "Vivid Pastel") {
    const t = themePalette();
    if (t && t.length) return t;
  }
  return PALETTES[options.palette] || PALETTE;
}

function grad(base) {
  return { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: base }, { offset: 1, color: base + "55" }] };
}

// Resolve a symbol for image-in-chart widgets
function imageSymbol(options) {
  if (options.imageUrl && String(options.imageUrl).trim()) return "image://" + options.imageUrl.trim();
  return null;
}

// Subtle watermark/background image inside the chart area
function bgGraphic(options) {
  if (!options.bgImageUrl || !String(options.bgImageUrl).trim()) return undefined;
  return [{
    type: "image",
    left: "center", top: "middle",
    z: -10, silent: true,
    style: {
      image: options.bgImageUrl.trim(),
      width: 220, height: 220,
      opacity: typeof options.bgImageOpacity === "number" ? options.bgImageOpacity : 0.08,
    },
  }];
}

// Regional / locale-aware number formatting (set per-dashboard from canvas.locale)
// Item 20: two layers — dashboard override (canvas.locale) on top of the effective regional
// defaults (user > tenant > platform) applied by RegionalProvider / public viewer.
let FMT_LOCALE;
const FMT_DEFAULTS = { locale: undefined, currency: undefined, numberSystem: "intl", dateFormat: "", timezone: "", firstDay: 1 };
export function setFormatLocale(l) { FMT_LOCALE = l || undefined; }
export function setFormatDefaults(d = {}) {
  FMT_DEFAULTS.locale = d.locale || undefined;
  FMT_DEFAULTS.currency = d.currency || undefined;
  FMT_DEFAULTS.numberSystem = d.numberSystem === "indian" ? "indian" : "intl";
  FMT_DEFAULTS.dateFormat = d.dateFormat || "";
  FMT_DEFAULTS.timezone = d.timezone || "";
  FMT_DEFAULTS.firstDay = [0, 1, 6].includes(Number(d.firstDay)) ? Number(d.firstDay) : 1;
}
export function getFormatDefaults() { return { ...FMT_DEFAULTS, effectiveLocale: FMT_LOCALE || FMT_DEFAULTS.locale }; }
const effLocale = (locale) => locale || FMT_LOCALE || FMT_DEFAULTS.locale;

export function formatNumber(v, locale) {
  const loc = effLocale(locale);
  if (v === null || v === undefined || v === "") return "\u2014";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const abs = Math.abs(n);
  if (FMT_DEFAULTS.numberSystem === "indian") {
    if (abs >= 1e7) return (n / 1e7).toFixed(2) + "Cr";
    if (abs >= 1e5) return (n / 1e5).toFixed(2) + "L";
  } else {
    if (abs >= 1e9) return (n / 1e9).toFixed(2) + "B";
    if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
  }
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return Number.isInteger(n) ? n.toLocaleString(loc) : n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CURRENCY_SYMBOL = { USD: "$", EUR: "\u20AC", GBP: "\u00A3", INR: "\u20B9", JPY: "\u00A5", CNY: "\u00A5", AUD: "A$", CAD: "C$", AED: "AED ", KES: "KSh ", SAR: "SAR ", QAR: "QAR ", NGN: "\u20A6", ZAR: "R" };
export const currencySymbol = (code) => CURRENCY_SYMBOL[code] || (code ? code + " " : "");

function _compact(n, decimals, locale) {
  const loc = effLocale(locale);
  const abs = Math.abs(n);
  const d = Number.isInteger(decimals) ? decimals : null;
  if (FMT_DEFAULTS.numberSystem === "indian") {
    if (abs >= 1e7) return (n / 1e7).toFixed(d ?? 2) + "Cr";
    if (abs >= 1e5) return (n / 1e5).toFixed(d ?? 2) + "L";
  } else {
    if (abs >= 1e9) return (n / 1e9).toFixed(d ?? 2) + "B";
    if (abs >= 1e6) return (n / 1e6).toFixed(d ?? 2) + "M";
  }
  if (abs >= 1e3) return (n / 1e3).toFixed(d ?? 1) + "K";
  return Number.isInteger(n) ? n.toLocaleString(loc) : n.toLocaleString(loc, { minimumFractionDigits: d ?? 2, maximumFractionDigits: d ?? 2 });
}

const FIXED_SCALES = { k: [1e3, "K"], m: [1e6, "M"], b: [1e9, "B"], lakh: [1e5, "L"], crore: [1e7, "Cr"] };

// Number scaling: fixed (K/M/B/Lakh/Crore), "auto" (K/M/B) or "auto_in" (K/L/Cr Indian system)
function _scaled(n, scale, dec, locale) {
  const loc = effLocale(locale);
  if (scale === "auto") return _compact(n, dec, loc);
  if (scale === "auto_in") {
    const abs = Math.abs(n);
    if (abs >= 1e7) return (n / 1e7).toFixed(dec ?? 2) + "Cr";
    if (abs >= 1e5) return (n / 1e5).toFixed(dec ?? 2) + "L";
    if (abs >= 1e3) return (n / 1e3).toFixed(dec ?? 1) + "K";
    return n.toLocaleString(loc, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  }
  const f = FIXED_SCALES[scale];
  if (f) return (n / f[0]).toFixed(dec ?? 2) + f[1];
  return n.toLocaleString(loc, { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Item 83: duration (value in seconds) -> h:mm:ss
function _duration(n) {
  const neg = n < 0 ? "-" : "";
  let s = Math.round(Math.abs(n));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  s = s % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return `${neg}${h}:${pad(m)}:${pad(s)}`;
}

// Item 83: nearest Excel-style fraction (denominator up to 16)
function _fraction(n, loc) {
  const neg = n < 0 ? "-" : "";
  const a = Math.abs(n);
  let whole = Math.floor(a);
  const frac = a - whole;
  const wtxt = () => whole.toLocaleString(effLocale(loc));
  if (frac < 1e-9) return `${neg}${wtxt()}`;
  let bn = Math.round(frac), bd = 1, be = Math.abs(frac - bn);
  for (let d = 2; d <= 16; d++) {
    const num = Math.round(frac * d);
    const err = Math.abs(frac - num / d);
    if (err < be - 1e-12) { be = err; bn = num; bd = d; }
  }
  if (bn >= bd) { whole += 1; bn = 0; }
  if (!bn) return `${neg}${wtxt()}`;
  return `${neg}${whole ? `${wtxt()} ` : ""}${bn}/${bd}`;
}

// Format a value using a semantic-layer FormatSpec
// ({ type, decimals, currency, compact, scale, prefix, suffix, negStyle }).
// Types: number | currency | percent | accounting | scientific | fraction | duration.
export function formatValue(v, fmt, locale) {
  const loc = effLocale(locale);
  if (v === null || v === undefined || v === "") return "\u2014";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (!fmt || (!fmt.type && !fmt.scale && !fmt.prefix && !fmt.suffix)) return formatNumber(v, loc);
  const isAcct = fmt.type === "accounting";
  const dec = Number.isInteger(fmt.decimals) ? fmt.decimals : (isAcct ? 2 : 0);
  const pre = fmt.prefix || "";
  const suf = fmt.suffix || "";
  const scale = fmt.scale || (fmt.compact ? "auto" : "");
  if (fmt.type === "duration") return `${pre}${_duration(n)}${suf}`;
  const paren = (isAcct || fmt.negStyle === "paren") && n < 0;
  const wrap = (txt) => (paren ? `(${txt})` : txt);
  const an = paren ? Math.abs(n) : n;
  if (fmt.type === "scientific") return `${pre}${wrap(an.toExponential(Math.max(2, Math.min(10, Number.isInteger(fmt.decimals) ? dec : 2))))}${suf}`;
  if (fmt.type === "fraction") return `${pre}${wrap(_fraction(an, loc))}${suf}`;
  if (fmt.type === "percent") {
    const p = an * 100;
    const body = scale ? _scaled(p, scale, dec, loc) : p.toLocaleString(loc, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    return `${pre}${wrap(`${body}%`)}${suf}`;
  }
  // Item 20: currency measures without an explicit code follow the effective regional currency
  const code = fmt.currency || ((fmt.type === "currency" || isAcct) ? FMT_DEFAULTS.currency : "");
  const sym = (fmt.type === "currency" || isAcct)
    ? (CURRENCY_SYMBOL[code] || (code ? code + " " : (isAcct ? "$" : "")))
    : "";
  const body = scale ? _scaled(an, scale, dec, loc) : an.toLocaleString(loc, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  return `${pre}${wrap(`${sym}${body}`)}${suf}`;
}

// Display-unit formatter for axis labels & data labels (item 102). Returns null when unit is off.
export function unitFormatter(unit, decimals) {
  if (!unit || unit === "none") return null;
  const dec = (decimals === "" || decimals === null || decimals === undefined) ? undefined : Number(decimals);
  return (v) => { const n = Number(v); return isNaN(n) ? String(v) : _scaled(n, unit, dec); };
}

// Post-process an ECharts option with per-widget legend controls (position, font, labels, hidden series).
export function applyLegendOptions(option, o = {}) {
  if (!option || !option.legend) return option;
  const lg = option.legend;
  const pos = o.legendPos || "bottom";
  delete lg.bottom; delete lg.top; delete lg.left; delete lg.right; delete lg.orient;
  if (pos === "top") lg.top = 0;
  else if (pos === "left") { lg.left = 0; lg.top = "middle"; lg.orient = "vertical"; }
  else if (pos === "right") { lg.right = 0; lg.top = "middle"; lg.orient = "vertical"; }
  else { lg.bottom = 0; lg.left = 8; lg.right = 90; }
  lg.textStyle = {
    ...(lg.textStyle || {}),
    ...(o.legendFontSize ? { fontSize: Number(o.legendFontSize) } : {}),
    ...(o.legendColor ? { color: o.legendColor } : {}),
  };
  const labels = o.seriesLabels || {};
  if (Object.keys(labels).length) lg.formatter = (name) => labels[name] || name;
  if (o.legendIcon && o.legendIcon !== "auto") lg.icon = o.legendIcon;
  const hidden = o.seriesHidden || [];
  if (hidden.length) lg.selected = { ...(lg.selected || {}), ...Object.fromEntries(hidden.map((n) => [n, false])) };
  if (option.grid && lg.show !== false) {
    // Items 32/34: keep the legend clear of axis titles and the data-zoom slider
    const xa = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
    const ya = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
    const bottomTitled = !!(xa && xa.name && xa.nameLocation === "middle");
    const leftTitled = !!(ya && ya.name && ya.nameLocation === "middle");
    const hasZoom = Array.isArray(option.dataZoom) && option.dataZoom.some((z) => z && z.type === "slider");
    if (pos === "top") option.grid = { ...option.grid, top: 46 };
    if (pos === "left") option.grid = { ...option.grid, left: leftTitled ? 118 : 96 };
    if (pos === "right") option.grid = { ...option.grid, right: 110 };
    if (pos === "bottom") {
      let extra = 0;
      if (bottomTitled) extra += 24;
      if (hasZoom) { extra += 22; lg.bottom = 22; }
      if (extra) option.grid = { ...option.grid, bottom: (option.grid.bottom || 36) + extra };
    }
  }
  return option;
}

const valueLabel = (show) => show ? { show: true, color: INK2(), fontSize: 10, fontFamily: FONT, formatter: (p) => formatNumber(p.value?.[1] ?? p.value ?? p.data?.value ?? p.data) } : { show: false };

const escH = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const hasV = (o, k) => o && o[k] !== undefined && o[k] !== null && o[k] !== "";

// Items 32/34: semantic names — key → display label from the query result meta (dimensions + measures)
export function labelsFromMeta(data) {
  const out = {};
  const meta = (data && data.meta) || {};
  [...(meta.dimensions || []), ...(meta.measures || [])].forEach((m) => { if (m && m.key && m.label && m.label !== m.key) out[m.key] = m.label; });
  return out;
}

function hexToRgb(h) {
  const s = String(h || "").replace("#", "");
  const f = s.length === 3 ? s.split("").map((c) => c + c).join("") : s;
  return [parseInt(f.slice(0, 2), 16) || 0, parseInt(f.slice(2, 4), 16) || 0, parseInt(f.slice(4, 6), 16) || 0];
}

// Interpolate across an ordered list of hex colour stops at t ∈ [0, 1]
export function lerpStops(stops, t) {
  const st = (stops || []).filter(Boolean);
  if (!st.length) return "#888888";
  if (st.length === 1) return st[0];
  const x = Math.max(0, Math.min(1, Number(t) || 0)) * (st.length - 1);
  const i = Math.min(st.length - 2, Math.floor(x));
  const f = x - i;
  const a = hexToRgb(st[i]), b = hexToRgb(st[i + 1]);
  return `rgb(${a.map((v, k) => Math.round(v + (b[k] - v) * f)).join(",")})`;
}

// Readable text colour (dark ink / white) for a given fill colour
export function contrastInk(fill) {
  let rgb;
  const m = /^rgba?\(([^)]+)\)/.exec(String(fill || ""));
  if (m) rgb = m[1].split(",").slice(0, 3).map((v) => Number(v.trim()));
  else if (/^#/.test(String(fill || ""))) rgb = hexToRgb(fill);
  else return INK();
  const lum = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  return lum > 0.6 ? "#0A1029" : "#FFFFFF";
}

// Estimated rendered width of an axis label (px) at the default 12px font
const estW = (s) => String(s ?? "").length * 7 + 6;

// Sort category labels for heatmap axes: auto (as queried) | asc | desc | value_desc | value_asc
function sortCats(cats, mode, totals) {
  const m = mode || "auto";
  if (m === "none") return cats;
  const out = [...cats];
  if (m === "auto") { const kind = detectValueKind(out); return kind === "text" ? cats : out.sort(smartComparator(out, kind)); }
  if (m === "asc") out.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  else if (m === "desc") out.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  else if (m === "value_desc") out.sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
  else if (m === "value_asc") out.sort((a, b) => (totals[a] || 0) - (totals[b] || 0));
  return out;
}

// data = { columns: [...], rows: [{}] }
export function buildOption(type, data, spec = {}, optionsIn = {}) {
  syncChartTheme();
  const options = { ...DEFAULT_OPTIONS, ...optionsIn };
  // Pie/donut/funnel slice labels default ON unless explicitly disabled by the user.
  const sliceLabelsShow = optionsIn.showLabels === undefined ? true : !!optionsIn.showLabels;
  const rows = (data && data.rows) || [];
  const colors = getColors(options);
  const xKey = spec.x_axis || (data.columns && data.columns[0]);
  const yKeys = (spec.y_axis && spec.y_axis.length ? spec.y_axis : (data.columns || []).slice(1));
  const seriesKey = spec.series || null;
  const graphic = bgGraphic(options);

  const fmtByKey = {};
  (((data && data.meta) || {}).measures || []).forEach((m) => { if (m.key) fmtByKey[m.key] = m.format; });
  const unitFmt = unitFormatter(options.axisUnit, options.axisDecimals);
  // Item 20: per-widget locale override — captured by every tooltip/label closure below.
  const wloc = options.locale || undefined;
  const fmtVal = (v, key) => (unitFmt ? unitFmt(v) : (fmtByKey[key] ? formatValue(v, fmtByKey[key], wloc) : formatNumber(v, wloc)));
  // Item 100: legend visibility works for single-series too (Auto / Always / Hidden)
  const legendShow = (multi) => {
    if (options.legendMode === "show") return true;
    if (options.legendMode === "hide") return false;
    return options.showLegend && multi;
  };
  // Items 32/34: semantic names everywhere (legend, tooltip, axis titles) — widget rename > model label > key
  const labelByKey = labelsFromMeta(data);
  const labelOf = (k) => (options.seriesLabels || {})[k] || labelByKey[k] || k;
  // Item 28: tooltip-only renames (field / series → "show as") apply at runtime everywhere
  const ttRenameMap = Object.fromEntries((((options.tooltip || {}).labels) || [])
    .filter((l) => l && l.name && l.label).map((l) => [String(l.name), l.label]));
  const ttLabelOf = (k) => ttRenameMap[String(k)] || labelOf(k);
  // Independent per-part number formats ({unit, decimals, prefix, suffix, type}) layered over the measure format
  const fmtOverride = (f, key, compactDefault = false) => (v) => {
    const base = fmtByKey[key] || {};
    if (f && hasV(f, "unit") && f.unit !== "none") {
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v ?? "");
      const sym = (base.type === "currency" || base.type === "accounting") ? currencySymbol(base.currency || FMT_DEFAULTS.currency) : "";
      return `${f.prefix || ""}${sym}${_scaled(n, f.unit, hasV(f, "decimals") ? Number(f.decimals) : undefined, wloc)}${f.suffix || ""}`;
    }
    if (f && (hasV(f, "decimals") || f.prefix || f.suffix || f.type)) {
      return formatValue(v, { ...base, ...(hasV(f, "decimals") ? { decimals: Number(f.decimals) } : {}), ...(f.type ? { type: f.type } : {}), ...(f.prefix ? { prefix: f.prefix } : {}), ...(f.suffix ? { suffix: f.suffix } : {}) }, wloc);
    }
    if (compactDefault) {
      const n = Number(v);
      if (Object.keys(base).length) {
        const compact = !base.scale && Math.abs(n) >= 1e4;
        return formatValue(v, { ...base, scale: base.scale || (compact ? "auto" : ""), ...(compact ? { decimals: Math.max(Number.isInteger(base.decimals) ? base.decimals : 0, 1) } : {}) }, wloc);
      }
      return unitFmt ? unitFmt(v) : formatNumber(v, wloc);
    }
    return fmtVal(v, key);
  };
  const mkeyOf = {}; // cartesian series display name → measure key (for tooltip formatting)
  const rangeOf = {}; // range-column series name → low-bound measure key
  const cartesianTooltip = {
    ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow", shadowStyle: { color: "rgba(43,78,255,0.06)" } }, confine: true,
    formatter: (ps) => {
      const arr = (Array.isArray(ps) ? ps : [ps]).filter((p) => p && p.seriesName && !/^__/.test(p.seriesName) && !/\(base\)$/.test(p.seriesName));
      if (!arr.length) return "";
      const head = arr[0].axisValueLabel ?? arr[0].name ?? "";
      const lines = arr.map((p) => {
        const raw = Array.isArray(p.value) ? p.value[p.value.length - 1] : (p.value && typeof p.value === "object" ? p.value.value : p.value);
        const v = Number(raw);
        if (!Number.isFinite(v)) return "";
        const mk = mkeyOf[p.seriesName] || p.seriesName;
        const low = rangeOf[p.seriesName];
        const txt = low && rows[p.dataIndex] ? `${fmtVal(num(rows[p.dataIndex][low]), mk)} \u2013 ${fmtVal(num(rows[p.dataIndex][mk]), mk)}` : fmtVal(v, mk);
        return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">${p.marker || ""}<span>${escH(ttLabelOf(p.seriesName))}</span><span style="margin-left:auto;padding-left:14px;font-weight:600">${escH(txt)}</span></div>`;
      }).join("");
      return `<div style="font-weight:600;margin-bottom:4px">${escH(head)}</div>${lines}`;
    },
  };

  const common = { color: colors, tooltip: cartesianTooltip, legend: { ...baseLegend, show: legendShow(yKeys.length > 1 || !!seriesKey), formatter: (n) => labelOf(n) }, grid: baseGrid, animationDuration: 700, animationEasing: "cubicOut", graphic };

  if (type === "pie" || type === "donut") {
    const valueKey = yKeys[0];
    const fmtV = (v) => fmtVal(v, valueKey);
    let pieData = rows.map((r) => ({ name: String(r[xKey]), value: num(r[valueKey]) }));
    if (options.pieSort === "desc") pieData.sort((a, b) => b.value - a.value);
    else if (options.pieSort === "asc") pieData.sort((a, b) => a.value - b.value);
    // Items 184/16/18: radial or linear gradient slices + per-slice explode
    const pieGrad = (base) => options.pieGradientType === "linear"
      ? { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: hexA(base, 0.72) }, { offset: 1, color: base }] }
      : { type: "radial", x: 0.5, y: 0.5, r: 0.9, colorStops: [{ offset: 0, color: hexA(base, 0.7) }, { offset: 1, color: base }] };
    const explodeName = (options.pieExplodeSlice || "").trim();
    pieData = pieData.map((d, i) => {
      const base = seriesColorOf(options, d.name, i, colors);
      return {
        ...d,
        ...(explodeName && d.name === explodeName ? { selected: true } : {}),
        itemStyle: { color: options.pieGradient ? pieGrad(base) : base },
      };
    });
    const labelPos = options.pieLabelPos || "outside";
    // Item 186: semi donut — half circle anchored to the bottom; custom arcs via start/end angle
    const semi = !!options.semiDonut;
    const hasCustomArc = options.pieStartAngle !== undefined && options.pieStartAngle !== null && options.pieStartAngle !== "";
    const innerPct = options.pieInnerRadius !== undefined && options.pieInnerRadius !== "" ? Number(options.pieInnerRadius) : (type === "donut" ? (options.donutRadius || 45) : 0);
    const outerPct = options.pieOuterRadius !== undefined && options.pieOuterRadius !== "" ? Number(options.pieOuterRadius) : (semi ? 95 : 72);
    const semiInner = type === "donut" ? Math.max(50, innerPct + 12) : innerPct;
    const bColor = options.pieBorderColor || CARD_BG();
    const bWidth = options.pieBorderWidth !== undefined && options.pieBorderWidth !== "" ? Number(options.pieBorderWidth) : 2;
    const bRadius = options.pieBorderRadius !== undefined && options.pieBorderRadius !== "" ? Number(options.pieBorderRadius) : 8;
    const total = pieData.reduce((a, d) => a + (Number(d.value) || 0), 0);
    // Item 18: center KPI annotation for donut / semi-donut
    const gArr = [];
    if (graphic) gArr.push(...(Array.isArray(graphic) ? graphic : [graphic]));
    const centerMode = options.pieCenterMode || "";
    if (centerMode && centerMode !== "none" && (type === "donut" || semi)) {
      const txt = centerMode === "custom" ? (options.pieCenterText || "") : fmtV(total);
      const sub = centerMode === "custom" ? "" : (options.pieCenterLabel || "Total");
      gArr.push({
        type: "text", left: "center", top: semi ? "58%" : "40%", silent: true, z: 10,
        style: { text: sub ? `${txt}\n${sub}` : txt, textAlign: "center", fill: options.pieCenterColor || INK(), fontFamily: DISPLAY_FONT, fontSize: Number(options.pieCenterFontSize) || 22, lineHeight: 24, rich: {} },
      });
    }
    return {
      color: colors, graphic: gArr.length ? gArr : graphic,
      tooltip: { ...baseTooltip, trigger: "item", formatter: (p) => `${p.marker || ""}${p.name}: ${fmtV(p.value)} (${p.percent}%)` },
      legend: { ...baseLegend, show: legendShow(true), ...(options.legendIcon && options.legendIcon !== "auto" ? { icon: options.legendIcon } : {}) },
      series: [{
        type: "pie",
        radius: semi ? [`${semiInner}%`, `${outerPct}%`] : [`${innerPct}%`, `${outerPct}%`],
        center: semi ? ["50%", "72%"] : ["50%", "46%"], avoidLabelOverlap: true,
        ...(semi ? { startAngle: 180, endAngle: 360 } : {}),
        ...(!semi && hasCustomArc ? { startAngle: Number(options.pieStartAngle), ...(options.pieEndAngle !== undefined && options.pieEndAngle !== "" ? { endAngle: Number(options.pieEndAngle) } : {}) } : {}),
        ...(options.piePadAngle ? { padAngle: Number(options.piePadAngle) } : {}),
        selectedMode: "single", selectedOffset: Number(options.pieExplodeOffset) || 12,
        itemStyle: {
          borderRadius: bRadius, borderColor: bColor, borderWidth: bWidth,
          ...(options.pieShadow ? { shadowBlur: 14, shadowColor: "rgba(10,16,41,0.28)", shadowOffsetY: 4 } : {}),
        },
        label: {
          color: options.pieLabelColor || (labelPos === "inside" ? "#fff" : INK2()), fontFamily: FONT,
          ...(options.pieLabelFontSize ? { fontSize: Number(options.pieLabelFontSize) } : {}),
          show: sliceLabelsShow, position: labelPos, formatter: pieLabelFormatter(options.pieLabelMode, fmtV),
        },
        labelLayout: { hideOverlap: true },
        labelLine: { show: labelPos === "outside" },
        data: pieData,
      }],
    };
  }

  // Item 176: Tornado / butterfly — one category, two measures mirrored around zero
  if (type === "tornado") {
    const lK = yKeys[0], rK = yKeys[1] || yKeys[0];
    const cats = rows.map((r) => String(r[xKey]));
    const lColor = seriesColorOf(options, lK, 0, colors);
    const rColor = seriesColorOf(options, rK, 1, colors);
    const absFmt = (v, k) => fmtVal(Math.abs(Number(v) || 0), k);
    const ttRename = Object.fromEntries(((options.tooltip || {}).labels || []).filter((l) => l.name && l.label).map((l) => [l.name, l.label]));
    const sideTotal = {};
    [lK, rK].forEach((k) => { sideTotal[k] = rows.reduce((a, r) => a + Math.abs(num(r[k])), 0); });
    const lblMode = options.labelMode || "value";
    const lblFmt = (key) => (p) => {
      const v = Math.abs(Number(p.value) || 0);
      if (lblMode === "category") return p.name;
      if (lblMode === "series") return ttRename[key] || key;
      if (lblMode === "percent") return sideTotal[key] ? `${((v / sideTotal[key]) * 100).toFixed(1)}%` : "";
      if (lblMode === "value_percent") return `${absFmt(v, key)} (${sideTotal[key] ? ((v / sideTotal[key]) * 100).toFixed(1) : 0}%)`;
      return absFmt(v, key);
    };
    const barSide = (key, sign, color, pos, radius) => ({
      name: key, type: "bar", stack: "tornado",
      data: rows.map((r) => sign * Math.abs(num(r[key]))),
      itemStyle: { color: options.gradient ? gradientFill(color, options, true) : color, borderRadius: radius },
      barMaxWidth: Number(options.tornadoBarWidth) || 26,
      ...(options.barCatGap !== undefined && options.barCatGap !== null && options.barCatGap !== "" ? { barCategoryGap: `${Number(options.barCatGap)}%` } : {}),
      label: sliceLabelsShow || options.showLabels
        ? { show: true, position: options.labelPosition || pos, color: (options.labelPosition || pos) === "inside" ? "#fff" : INK2(), fontFamily: FONT, fontSize: 10, formatter: lblFmt(key) }
        : { show: false },
    });
    return {
      color: [lColor, rColor], graphic,
      tooltip: {
        ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" },
        formatter: (ps) => {
          const arr = Array.isArray(ps) ? ps : [ps];
          const head = (options.tooltip || {}).header || arr[0]?.name || "";
          return `<b>${head}</b><br/>` + arr.map((p) => `${p.marker || ""}${ttRename[p.seriesName] || p.seriesName}: ${absFmt(p.value, p.seriesName)}`).join("<br/>");
        },
      },
      legend: { ...baseLegend, show: legendShow(true) },
      grid: { ...baseGrid, left: 8 },
      xAxis: {
        type: "value",
        axisLabel: { ...AXIS_LABEL, formatter: (v) => (unitFmt ? unitFmt(Math.abs(v)) : formatNumber(Math.abs(v))) },
        axisLine: AXIS_LINE, splitLine: options.showGrid ? SPLIT_LINE : { show: false },
      },
      yAxis: {
        type: "category", data: cats, inverse: true,
        axisLabel: { ...AXIS_LABEL, width: 110, overflow: "truncate" },
        axisLine: options.tornadoAxisColor ? { lineStyle: { color: options.tornadoAxisColor, width: Number(options.tornadoAxisWidth) || 1 } } : AXIS_LINE,
        axisTick: { show: false },
      },
      series: [
        barSide(lK, -1, lColor, "left", [8, 0, 0, 8]),
        barSide(rK, 1, rColor, "right", [0, 8, 8, 0]),
      ],
    };
  }

  if (type === "scatter" || type === "bubble" || type === "image_scatter") {
    // Item 47: normalised bubble sizes, colour modes (category / single / series-dim / value gradient), semantic tooltips
    const xk = yKeys[0] || data.columns[1];
    const yk = yKeys[1] || data.columns[2] || yKeys[0];
    const sizeKey = yKeys[2];
    const isBubble = type === "bubble" || !!sizeKey;
    const sym = imageSymbol(options);
    const mSize = Number(options.markerSize) || 14;
    const mOpacity = typeof options.markerOpacity === "number" ? options.markerOpacity : (sym ? 1 : 0.82);
    const gridH = options.showGridH ?? options.showGrid;
    const gridV = options.showGridV ?? options.showGrid;
    const lm = options.scatterLabelMode || "name";
    const labelFmt = (p) => {
      const [xv, yv, , nm] = p.value || [];
      if (lm === "x") return fmtVal(xv, xk);
      if (lm === "y") return fmtVal(yv, yk);
      if (lm === "name_xy") return `${nm} (${fmtVal(xv, xk)}, ${fmtVal(yv, yk)})`;
      return nm;
    };
    const sizeMax = Math.max(...rows.map((r) => Math.abs(num(r[sizeKey] || 0))), 0) || 1;
    const bMin = Number(options.bubbleMin) || 8, bMax = Number(options.bubbleMax) || 40;
    const sizeOf = (d) => (isBubble ? bMin + (bMax - bMin) * Math.sqrt(Math.abs(num(d[2])) / sizeMax) : (sym ? (options.imageSize || 30) : mSize));
    const groups = seriesKey ? [...new Set(rows.map((r) => String(r[seriesKey])))] : [null];
    const colorMode = options.bubbleColorMode || (seriesKey ? "series" : (options.markerColor ? "single" : "category"));
    const stops = [options.heatLow || SEQ_LOW(), options.heatMid || colors[0], options.heatHigh || SEQ_HIGH()];
    const colorDim = options.bubbleColorDim === "y" ? 1 : (options.bubbleColorDim === "x" ? 0 : 2);
    const colorKey = colorDim === 1 ? yk : colorDim === 0 ? xk : (sizeKey || yk);
    const series = groups.map((g, gi) => {
      const pts = rows.filter((r) => g == null || String(r[seriesKey]) === g);
      return {
        name: g == null ? labelOf(yk) : g, type: "scatter",
        colorBy: colorMode === "category" && g == null ? "data" : "series",
        symbol: sym || options.markerShape || "circle",
        symbolSize: sizeOf,
        data: pts.map((r) => [num(r[xk]), num(r[yk]), num(r[sizeKey] || 0), String(r[xKey]), g]),
        itemStyle: {
          opacity: mOpacity,
          ...(colorMode === "single" && options.markerColor ? { color: options.markerColor } : (g != null ? { color: seriesColorOf(options, g, gi, colors) } : {})),
          ...(options.markerBorderColor ? { borderColor: options.markerBorderColor, borderWidth: Number(options.markerBorderWidth) || 1 } : {}),
        },
        emphasis: { focus: groups.length > 1 ? "series" : "none", scale: 1.15 },
        label: { show: !!options.showLabels && !sym, formatter: labelFmt, color: INK2(), fontFamily: FONT, position: "top" },
        labelLayout: { hideOverlap: true },
      };
    });
    const cvals = rows.map((r) => num(r[colorKey]));
    const valueMode = colorMode === "value";
    const legendPos = options.bubbleLegendPos || "right";
    const grid = { ...baseGrid, ...(valueMode && legendPos === "right" ? { right: 76 } : {}) };
    return {
      ...common, grid,
      legend: { ...baseLegend, show: legendShow(groups.length > 1), formatter: (n) => labelOf(n) },
      ...(valueMode ? {
        visualMap: {
          type: "continuous", dimension: colorDim, min: Math.min(...cvals, 0), max: Math.max(...cvals, 1), show: legendPos !== "hidden",
          inRange: { color: stops }, textStyle: { color: INK2(), fontSize: 10 }, formatter: (v) => fmtVal(v, colorKey),
          ...(legendPos === "bottom" ? { orient: "horizontal", left: "center", bottom: 0 } : { orient: "vertical", right: 0, top: "middle", itemHeight: 110 }),
        },
      } : {}),
      tooltip: {
        ...baseTooltip, trigger: "item", confine: true,
        formatter: (p) => {
          const [xv, yv, sz, nm, g] = p.value || [];
          const chip = `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${p.color};margin-right:6px"></span>`;
          const parts = [
            `<div style="font-weight:600;margin-bottom:2px">${chip}${escH(nm || p.seriesName || "")}</div>`,
            ...(g != null ? [`<div>${escH(ttLabelOf(seriesKey))}: <b>${escH(g)}</b></div>`] : []),
            `<div>${escH(ttLabelOf(xk))}: <b>${escH(fmtVal(xv, xk))}</b></div>`,
            `<div>${escH(ttLabelOf(yk))}: <b>${escH(fmtVal(yv, yk))}</b></div>`,
          ];
          if (isBubble && sizeKey) parts.push(`<div>${escH(ttLabelOf(sizeKey))} (size): <b>${escH(fmtVal(sz, sizeKey))}</b></div>`);
          return parts.join("");
        },
      },
      xAxis: applyAxisFmt({ type: "value", name: options.xTitle ?? labelOf(xk), nameLocation: "middle", nameGap: 30, nameTextStyle: { color: INK3(), fontFamily: FONT, fontSize: 11 }, ...valueAxisBounds(options.xMin, options.xMax), axisLabel: { ...AXIS_LABEL, hideOverlap: true, ...(unitFmt ? { formatter: unitFmt } : {}) }, axisLine: AXIS_LINE, splitLine: gridV ? SPLIT_LINE : { show: false } }, (options.axisFmt || {}).x),
      yAxis: applyAxisFmt({ type: "value", name: options.yTitle ?? labelOf(yk), nameLocation: "middle", nameGap: Math.max(44, estW(unitFmt ? unitFmt(Math.max(...rows.map((r) => Math.abs(num(r[yk]))), 0)) : formatNumber(Math.max(...rows.map((r) => Math.abs(num(r[yk]))), 0))) + 16), nameTextStyle: { color: INK3(), fontFamily: FONT, fontSize: 11 }, ...valueAxisBounds(options.yMin, options.yMax), axisLabel: { ...AXIS_LABEL, hideOverlap: true, ...(unitFmt ? { formatter: unitFmt } : {}) }, splitLine: gridH ? SPLIT_LINE : { show: false } }, (options.axisFmt || {}).y),
      series,
    };
  }

  if (type === "radar") {
    // Items 48/51-52 + #51: axis-name click interaction, focus/select, per-series styles, per-axis max, value labels, formatted tooltip
    const draw = options.radarDraw || "area";
    const rop = options.radarOpacity ?? 0.22;
    const rlw = Number(options.lineWidth) || 2.5;
    const marker = options.showMarkers === false ? "none" : (options.markerShape || "circle");
    const rKeys = yKeys.length ? yKeys : [data.columns[data.columns.length - 1]];
    const sharedMax = Number(options.radarMax) > 0
      ? Number(options.radarMax)
      : Math.max(...rows.flatMap((x) => rKeys.map((k) => num(x[k]))), 1) * 1.2;
    const perAxis = options.radarMaxMode === "per_axis";
    // Item 15: "per series" normalises every measure to its own maximum so measures with very
    // different magnitudes (e.g. revenue vs units) stay comparable; raw values stay in labels/tooltips.
    const perSeries = options.radarMaxMode === "per_series";
    const seriesMaxOf = {};
    rKeys.forEach((k) => { seriesMaxOf[k] = Math.max(...rows.map((r) => Math.abs(num(r[k]))), 0.0001); });
    const rawVals = {};
    rKeys.forEach((k) => { rawVals[k] = rows.map((r) => num(r[k])); });
    const axisFontR = { color: options.radarAxisColor || INK2(), fontFamily: CHART_FONT, fontSize: Number(options.radarAxisSize) || 11, ...(options.radarAxisBold ? { fontWeight: 600 } : {}) };
    const maxLen = Number(options.radarAxisMaxLen) || 18;
    const wrapName = (n) => {
      const s = String(n ?? "");
      if (s.length <= maxLen) return s;
      const lines = []; let cur = "";
      s.split(/\s+/).forEach((w) => { if ((cur + " " + w).trim().length > maxLen && cur) { lines.push(cur); cur = w; } else cur = (cur + " " + w).trim(); });
      if (cur) lines.push(cur);
      return lines.join("\n");
    };
    const indicators = rows.map((r) => ({
      name: String(r[xKey]),
      max: perSeries ? 100 : (perAxis ? Math.max(...rKeys.map((k) => num(r[k])), 0.0001) * 1.15 : sharedMax),
    }));
    const rs = options.radarSeries || {};
    const legendOn = legendShow(rKeys.length > 1);
    const radius = Number(options.radarRadius) > 0 ? `${Number(options.radarRadius)}%` : (options.radarAxisLabels === false ? "75%" : "62%");
    const seriesData = rKeys.map((k, i) => {
      const cfg = rs[k] || {};
      const base = cfg.color || seriesColorOf(options, k, i, colors);
      const sDraw = cfg.draw || draw;
      return {
        value: rows.map((r) => (perSeries ? Math.round((num(r[k]) / seriesMaxOf[k]) * 1000) / 10 : num(r[k]))), name: k,
        areaStyle: sDraw === "area" ? { opacity: hasV(cfg, "opacity") ? Number(cfg.opacity) : rop, color: base } : undefined,
        lineStyle: { color: base, width: Number(cfg.width) || rlw, type: cfg.dash || "solid" },
        itemStyle: { color: base },
        symbol: cfg.marker === "none" ? "none" : (cfg.marker || marker),
        symbolSize: Number(cfg.markerSize) || Number(options.markerSize) || 6,
        ...(options.showLabels || cfg.labels ? { label: { show: true, color: base, fontSize: Number(options.radarLabelSize) || 10, fontFamily: CHART_FONT, formatter: (p) => fmtVal(perSeries ? rawVals[k][p.dimensionIndex ?? 0] : p.value, k) } } : {}),
      };
    });
    return {
      color: colors, graphic,
      tooltip: {
        ...baseTooltip, trigger: "item", confine: true,
        formatter: (p) => {
          const vals = Array.isArray(p.value) ? p.value : [];
          const raw = rawVals[p.name] || vals;
          const head = `<div style="font-weight:600;margin-bottom:3px">${p.marker || ""}${escH(ttLabelOf(p.name))}</div>`;
          return head + indicators.map((ind, i) => `<div style="display:flex;gap:10px"><span>${escH(ind.name)}</span><span style="margin-left:auto;font-weight:600">${escH(fmtVal(perSeries ? raw[i] : vals[i], p.name))}${perSeries ? ` <span style="opacity:.6">(${(vals[i] ?? 0).toFixed(0)}%)</span>` : ""}</span></div>`).join("");
        },
      },
      legend: { ...baseLegend, show: legendOn, formatter: (n) => labelOf(n) },
      radar: {
        shape: options.radarShape === "circle" ? "circle" : "polygon",
        indicator: indicators, radius, center: ["50%", legendOn ? "48%" : "50%"],
        splitNumber: Math.max(2, Math.min(10, Number(options.radarSplits) || 5)),
        triggerEvent: true,
        axisName: options.radarAxisLabels === false ? { show: false } : { ...axisFontR, formatter: (n) => wrapName(n) },
        axisLine: { lineStyle: { color: options.radarGridColor || "rgba(15,23,42,0.12)" } },
        splitLine: { show: options.radarSplitLines !== false, lineStyle: { color: options.radarGridColor || "rgba(15,23,42,0.08)" } },
        splitArea: { show: options.radarSplitArea !== false, areaStyle: { color: ["rgba(43,78,255,0.03)", "rgba(43,78,255,0.06)"] } },
        axisLabel: options.radarScaleLabels
          ? { show: true, color: INK3(), fontSize: 9, fontFamily: CHART_FONT, showMinLabel: false, formatter: (v) => (perSeries ? `${Math.round(v)}%` : fmtOverride(null, rKeys[0], true)(v)) }
          : { show: false },
      },
      series: [{
        type: "radar",
        symbol: marker === "none" ? "none" : marker,
        symbolSize: Number(options.markerSize) || 6,
        emphasis: options.radarFocus === false ? { disabled: true } : { focus: "self", lineStyle: { width: rlw + 1.5 }, areaStyle: { opacity: Math.min(0.6, rop + 0.2) } },
        blur: options.radarFocus === false ? {} : { lineStyle: { opacity: 0.25 }, areaStyle: { opacity: 0.05 }, itemStyle: { opacity: 0.3 } },
        selectedMode: options.radarSelect ? "multiple" : false,
        select: { lineStyle: { width: rlw + 2 }, areaStyle: { opacity: Math.min(0.7, rop + 0.3) } },
        data: seriesData,
      }],
    };
  }

  if (type === "funnel") {
    // Items 119-121 + 43: label content/position, stage colors, gradient, sorting, stage mapping
    const valueKey = yKeys[0];
    const fmtV = (v) => fmtVal(v, valueKey);
    // Item 43: dynamic category → stage mapping (rename / merge / order / Other / exclude)
    let frows = rows.map((r) => ({ name: String(r[xKey]), value: num(r[valueKey]) }));
    const stages = (options.funnelStages || []).filter((s) => s && s.label);
    if (stages.length) {
      const valMap = {};
      stages.forEach((s, si) => {
        const vals = Array.isArray(s.values) ? s.values : String(s.values || "").split(",");
        vals.forEach((v) => { const k = String(v).trim().toLowerCase(); if (k) valMap[k] = si; });
      });
      const acc = stages.map((s) => ({ name: s.label, value: 0, __color: s.color || "" }));
      let other = 0;
      const rest = [];
      frows.forEach((r) => {
        const si = valMap[r.name.trim().toLowerCase()];
        if (si !== undefined) acc[si].value += r.value;
        else if ((options.funnelUnmapped || "keep") === "other") other += r.value;
        else if (options.funnelUnmapped === "exclude") { /* dropped */ }
        else rest.push(r);
      });
      frows = [...acc, ...rest];
      if (other > 0) frows.push({ name: options.funnelOtherLabel || "Other", value: other });
    }
    const fdata = frows.map((r, i) => {
      const base = r.__color || seriesColorOf(options, r.name, i, colors);
      return { name: r.name, value: r.value, itemStyle: { color: options.funnelGradient ? gradientFill(base, options) : base } };
    });
    const maxV = Math.max(...fdata.map((d) => d.value), 0);
    const pos = options.funnelLabelPos || "inside";
    return {
      color: colors, graphic,
      tooltip: { ...baseTooltip, trigger: "item", formatter: (p) => `${p.name}: ${fmtV(p.value)}${maxV ? ` (${((p.value / maxV) * 100).toFixed(1)}%)` : ""}` },
      legend: { ...baseLegend, show: legendShow(true) },
      series: [{
        type: "funnel", left: "10%", right: "10%", top: 10, bottom: 30,
        sort: options.funnelSort === "ascending" ? "ascending" : options.funnelSort === "none" ? "none" : "descending",
        gap: Number(options.funnelGap ?? 2),
        label: { show: sliceLabelsShow, position: pos, color: pos === "inside" ? "#fff" : INK2(), fontFamily: FONT, formatter: funnelLabelFormatter(options.funnelLabelMode, maxV, fmtV) },
        labelLine: { show: pos !== "inside" },
        itemStyle: { borderColor: CARD_BG(), borderWidth: 2, borderRadius: 6 },
        data: fdata,
      }],
    };
  }

  if (type === "treemap") {
    // Items 129-130: parent-child hierarchy, tile colors, borders, padding, sorting
    const valueKey = yKeys[0];
    const fmtV = (v) => fmtVal(v, valueKey);
    const dimKeys = (spec.dims && spec.dims.length ? spec.dims : [xKey, seriesKey]).filter(Boolean);
    const build = (rs, depth) => {
      const g = {};
      rs.forEach((r) => { const k = String(r[dimKeys[depth]]); (g[k] = g[k] || []).push(r); });
      return Object.entries(g).map(([name, rr]) => {
        const node = { name, value: rr.reduce((a, r) => a + num(r[valueKey]), 0) };
        if (depth < dimKeys.length - 1) node.children = build(rr, depth + 1);
        return node;
      });
    };
    // Item 48: label size / visibility / content scale with the tile's share of the total
    const shareTotal = build(rows, 0).reduce((a, n) => a + n.value, 0) || 1;
    const scaleMode = options.treemapLabelScale || "fixed";
    const fMin = Number(options.treemapLabelMin) || 9, fMax = Number(options.treemapLabelMax) || 15;
    const hidePct = Number(options.treemapHideSmallPct) || 0;
    const valMode = options.treemapValueMode || (options.treemapShowValues ? "value" : "name");
    const lblColorMode = options.treemapLabelColor || "auto";
    const fontFor = (share) => (scaleMode === "auto" ? Math.max(fMin, Math.min(fMax, Math.round(fMin + 20 * Math.sqrt(share)))) : (Number(options.treemapLabelFontSize) || 12));
    const textFor = (name, value) => {
      const p = `${((value / shareTotal) * 100).toFixed(1)}%`;
      if (valMode === "value") return `${name}\n${fmtV(value)}`;
      if (valMode === "percent") return `${name}\n${p}`;
      if (valMode === "value_percent") return `${name}\n${fmtV(value)} (${p})`;
      if (valMode === "value_only") return fmtV(value);
      return name;
    };
    const decorate = (n, color) => {
      // Item 13: only leaves get an inside label — parents show their name (and value) in the
      // header band, which children would otherwise cover.
      const share = n.value / shareTotal;
      if (n.children) {
        const node = { ...n, children: n.children.map((c) => decorate(c, color)) };
        return node;
      }
      return {
        ...n,
        label: {
          show: !(hidePct > 0 && share * 100 < hidePct),
          fontSize: fontFor(share),
          color: lblColorMode === "auto" ? contrastInk(color) : lblColorMode,
          formatter: () => textFor(n.name, n.value),
          overflow: options.treemapLabelOverflow || "truncate",
        },
      };
    };
    const treeData = build(rows, 0).map((n, i) => { const c = seriesColorOf(options, n.name, i, colors); return decorate({ ...n, itemStyle: { color: c } }, c); });
    const pad = Number(options.treemapPadding ?? 2);
    const bColor = options.treemapBorderColor || CARD_BG();
    const bWidth = Number(options.treemapBorderWidth ?? 2);
    const sort = options.treemapSort === "asc" ? "asc" : options.treemapSort === "none" ? null : "desc";
    const hasHierarchy = dimKeys.length > 1;
    const upperShow = hasHierarchy && options.treemapUpperLabel !== false;
    const tLabelPos = options.treemapLabelPos || "inside";
    const upperH = Math.max(14, Number(options.treemapUpperLabelSize) || 22);
    // Item 13: parent header shows the group name and (optionally) its total
    const upperFmt = (p) => (options.treemapUpperValues ? `${p.name}  ${fmtV(p.value)}` : p.name);
    const upperCfg = {
      show: upperShow, height: upperH, color: options.treemapUpperLabelColor || "#fff",
      fontFamily: FONT, fontSize: Math.max(9, upperH - 11), fontWeight: 600, formatter: upperFmt,
    };
    const levels = hasHierarchy
      ? [
        { itemStyle: { borderWidth: 0, gapWidth: pad }, upperLabel: { show: false } },
        { itemStyle: { borderColor: bColor, borderWidth: Math.max(bWidth, 2), gapWidth: pad, borderRadius: 6 }, upperLabel: upperCfg },
        { itemStyle: { borderColor: bColor, borderWidth: Math.max(1, bWidth - 1), gapWidth: Math.max(1, Math.round(pad / 2)) }, colorSaturation: [0.35, 0.65] },
      ]
      : [
        { itemStyle: { borderColor: bColor, borderWidth: bWidth, gapWidth: pad, borderRadius: 6 } },
      ];
    return {
      tooltip: { ...baseTooltip, formatter: (p) => `${escH(p.name)}: ${fmtV(p.value)}${shareTotal ? ` (${((p.value / shareTotal) * 100).toFixed(1)}%)` : ""}` }, graphic,
      series: [{
        type: "treemap", roam: false, nodeClick: false, sort,
        // Item 13: fill the widget instead of ECharts' default 10% inset
        left: 2, right: 2, bottom: 2, top: options.treemapBreadcrumb ? 22 : 2,
        breadcrumb: { show: !!options.treemapBreadcrumb, top: 0, itemStyle: { color: "rgba(15,23,42,0.65)", textStyle: { fontFamily: FONT, fontSize: 10 } } },
        label: { color: "#fff", fontSize: Number(options.treemapLabelFontSize) || 12, fontFamily: FONT, position: tLabelPos, formatter: options.treemapShowValues ? (p) => `${p.name}\n${fmtV(p.value)}` : "{b}" },
        upperLabel: upperCfg,
        levels,
        data: treeData,
      }],
    };
  }

  if (type === "sunburst") {
    // Hierarchy from dimension(xKey) -> series dim(seriesKey) -> value
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const secondDim = seriesKey || (data.columns || []).find((c) => c !== xKey && c !== valueKey);
    const groups = {};
    rows.forEach((r) => {
      const k = String(r[xKey]);
      groups[k] = groups[k] || { name: k, children: [], value: 0 };
      if (secondDim) groups[k].children.push({ name: String(r[secondDim]), value: num(r[valueKey]) });
      groups[k].value += num(r[valueKey]);
    });
    const treeData = Object.values(groups).map((g, i) => ({
      name: g.name, value: g.value,
      itemStyle: { color: colors[i % colors.length] },
      children: secondDim ? g.children : undefined,
    }));
    return {
      tooltip: { ...baseTooltip, formatter: (p) => `${p.name}: ${formatNumber(p.value)}` }, graphic,
      series: [{
        type: "sunburst", radius: ["12%", "92%"], data: treeData,
        label: { color: "#fff", fontFamily: FONT, minAngle: 8 },
        itemStyle: { borderColor: CARD_BG(), borderWidth: 2, borderRadius: 4 },
        emphasis: { focus: "ancestor" },
      }],
    };
  }

  if (type === "sankey") {
    // source = xKey, target = seriesKey (or 2nd column), value = measure
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const targetKey = seriesKey || (data.columns || []).find((c) => c !== xKey && c !== valueKey);
    const nodeSet = new Set();
    const links = [];
    rows.forEach((r) => {
      const s = "\u25B8 " + String(r[xKey]);
      const t = String(r[targetKey]);
      nodeSet.add(s); nodeSet.add(t);
      links.push({ source: s, target: t, value: num(r[valueKey]) });
    });
    const nodes = [...nodeSet].map((n, i) => ({ name: n, itemStyle: { color: colors[i % colors.length] } }));
    return {
      tooltip: { ...baseTooltip, trigger: "item", formatter: (p) => p.dataType === "edge" ? `${p.data.source} \u2192 ${p.data.target}: ${formatNumber(p.data.value)}` : p.name }, graphic,
      series: [{
        type: "sankey", left: 12, right: 12, top: 16, bottom: 16,
        nodeWidth: 14, nodeGap: 10, emphasis: { focus: "adjacency" },
        lineStyle: { color: "gradient", opacity: 0.45, curveness: 0.5 },
        label: { color: INK2(), fontFamily: FONT, fontSize: 11 },
        data: nodes, links,
      }],
    };
  }

  if (type === "graph") {
    // network: source = xKey, target = seriesKey/2nd col, weight = measure
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const targetKey = seriesKey || (data.columns || []).find((c) => c !== xKey && c !== valueKey);
    const deg = {};
    const links = rows.map((r) => {
      const s = String(r[xKey]); const t = String(r[targetKey]); const v = num(r[valueKey]);
      deg[s] = (deg[s] || 0) + v; deg[t] = (deg[t] || 0) + v;
      return { source: s, target: t, value: v, lineStyle: { width: Math.max(1, Math.sqrt(v) / 3) } };
    });
    const names = Object.keys(deg);
    const maxDeg = Math.max(...Object.values(deg), 1);
    const nodes = names.map((n, i) => ({ name: n, value: deg[n], symbolSize: 14 + (deg[n] / maxDeg) * 44, itemStyle: { color: colors[i % colors.length] } }));
    return {
      tooltip: { ...baseTooltip, formatter: (p) => p.dataType === "edge" ? `${p.data.source} \u2014 ${p.data.target}` : `${p.name}: ${formatNumber(p.value)}` }, graphic,
      series: [{
        type: "graph", layout: "force", roam: true, draggable: true,
        force: { repulsion: 220, edgeLength: [60, 160], gravity: 0.08 },
        label: { show: true, color: INK2(), fontFamily: FONT, position: "right", fontSize: 11 },
        lineStyle: { color: "source", opacity: 0.4, curveness: 0.12 },
        emphasis: { focus: "adjacency", lineStyle: { width: 4 } },
        data: nodes, links,
      }],
    };
  }

  if (type === "candlestick") {
    // x = date dim; measures order: [open, close, low, high]
    const [oK, cK, lK, hK] = yKeys;
    const cats = rows.map((r) => String(r[xKey]));
    const cdata = rows.map((r) => [num(r[oK]), num(r[cK]), num(r[lK]), num(r[hK])]);
    return {
      tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "cross" } }, graphic,
      grid: { ...baseGrid, bottom: 48 },
      xAxis: { type: "category", data: cats, axisLabel: { ...AXIS_LABEL, rotate: cats.length > 8 ? 30 : 0 }, axisLine: AXIS_LINE },
      yAxis: { type: "value", scale: true, axisLabel: AXIS_LABEL, splitLine: options.showGrid ? SPLIT_LINE : { show: false } },
      series: [{
        type: "candlestick", data: cdata,
        itemStyle: { color: "#00C2A8", color0: "#E24B5B", borderColor: "#00C2A8", borderColor0: "#E24B5B" },
      }],
    };
  }

  if (type === "waterfall") {
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const cats = rows.map((r) => String(r[xKey]));
    const vals = rows.map((r) => num(r[valueKey]));
    const base = []; const pos = []; const neg = []; let run = 0;
    vals.forEach((v) => {
      base.push(v >= 0 ? run : run + v);
      if (v >= 0) { pos.push(v); neg.push("-"); } else { pos.push("-"); neg.push(-v); }
      run += v;
    });
    return {
      ...common, legend: { ...baseLegend, show: false },
      tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" }, formatter: (ps) => { const i = ps[0].dataIndex; return `${cats[i]}: ${formatNumber(vals[i])}<br/><span style="color:#51607A">running ${formatNumber(vals.slice(0, i + 1).reduce((a, b) => a + b, 0))}</span>`; } },
      xAxis: { type: "category", data: cats, axisLabel: { ...AXIS_LABEL, rotate: cats.length > 8 ? 30 : 0 }, axisLine: AXIS_LINE, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: AXIS_LABEL, splitLine: options.showGrid ? SPLIT_LINE : { show: false } },
      series: [
        { name: "base", type: "bar", stack: "wf", itemStyle: { color: "transparent" }, emphasis: { itemStyle: { color: "transparent" } }, data: base, silent: true },
        { name: "Increase", type: "bar", stack: "wf", itemStyle: { color: grad(colors[1] || "#00C2A8"), borderRadius: [8, 8, 0, 0] }, label: { show: !!options.showLabels, position: "top", color: INK2(), fontSize: 10, fontFamily: FONT, formatter: (p) => p.value === "-" ? "" : formatNumber(p.value) }, data: pos },
        { name: "Decrease", type: "bar", stack: "wf", itemStyle: { color: grad("#E24B5B"), borderRadius: [0, 0, 8, 8] }, label: { show: !!options.showLabels, position: "bottom", color: INK2(), fontSize: 10, fontFamily: FONT, formatter: (p) => p.value === "-" ? "" : formatNumber(p.value) }, data: neg },
      ],
    };
  }

  if (type === "boxplot") {
    const [minK, q1K, medK, q3K, maxK] = yKeys;
    const cats = rows.map((r) => String(r[xKey]));
    const bdata = rows.map((r) => [num(r[minK]), num(r[q1K]), num(r[medK]), num(r[q3K]), num(r[maxK])]);
    return {
      ...common, legend: { ...baseLegend, show: false },
      tooltip: { ...baseTooltip, trigger: "item", formatter: (p) => { const d = Array.isArray(p.data) ? p.data : []; const a = d.length === 6 ? d.slice(1) : d; return `${p.name}<br/>max ${formatNumber(a[4])} \u00B7 Q3 ${formatNumber(a[3])} \u00B7 med ${formatNumber(a[2])} \u00B7 Q1 ${formatNumber(a[1])} \u00B7 min ${formatNumber(a[0])}`; } },
      xAxis: { type: "category", data: cats, axisLabel: { ...AXIS_LABEL, rotate: cats.length > 8 ? 30 : 0 }, axisLine: AXIS_LINE, axisTick: { show: false } },
      yAxis: { type: "value", scale: true, axisLabel: AXIS_LABEL, splitLine: options.showGrid ? SPLIT_LINE : { show: false } },
      series: [{ type: "boxplot", data: bdata, itemStyle: { color: colors[0] + "26", borderColor: colors[0], borderWidth: 2 }, emphasis: { itemStyle: { borderColor: colors[2] || colors[0], borderWidth: 2.5 } } }],
    };
  }

  if (type === "calendar_heatmap") {
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const cd = rows.map((r) => [String(r[xKey]), num(r[valueKey])]).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d[0]));
    const dates = cd.map((d) => d[0]).sort();
    let range = new Date().getFullYear().toString();
    if (dates.length) {
      const y0 = dates[0].slice(0, 4); const y1 = dates[dates.length - 1].slice(0, 4);
      range = y0 === y1 ? y0 : [dates[0], dates[dates.length - 1]];
    }
    const maxV = Math.max(...cd.map((d) => d[1]), 1);
    return {
      tooltip: { ...baseTooltip, formatter: (p) => `${p.value[0]}: ${formatNumber(p.value[1])}` }, graphic,
      visualMap: { min: 0, max: maxV, calculable: true, orient: "horizontal", left: "center", bottom: 4, itemWidth: 12, inRange: { color: [SEQ_LOW(), colors[0], SEQ_HIGH()] }, textStyle: { color: INK2() } },
      calendar: { top: 40, left: 36, right: 20, bottom: 60, cellSize: ["auto", 15], range, itemStyle: { borderColor: CARD_BG(), borderWidth: 2, color: "#EEF3F9" }, splitLine: { lineStyle: { color: "rgba(15,23,42,0.14)" } }, yearLabel: { show: true, color: INK3(), fontFamily: DISPLAY_FONT }, dayLabel: { color: INK3(), fontFamily: FONT }, monthLabel: { color: INK3(), fontFamily: FONT } },
      series: [{ type: "heatmap", coordinateSystem: "calendar", data: cd, itemStyle: { borderRadius: 2 } }],
    };
  }

  if (type === "wordcloud") {
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    return {
      tooltip: { ...baseTooltip, formatter: (p) => `${p.name}: ${formatNumber(p.value)}` }, graphic,
      series: [{
        type: "wordCloud", shape: "circle", left: "center", top: "center",
        width: "96%", height: "92%", sizeRange: [14, 64], rotationRange: [-45, 45],
        gridSize: 8, drawOutOfBound: false,
        textStyle: { fontFamily: DISPLAY_FONT, fontWeight: 600, color: () => colors[Math.floor(Math.random() * colors.length)] },
        emphasis: { textStyle: { fontWeight: 700 } },
        data: rows.map((r) => ({ name: String(r[xKey]), value: num(r[valueKey]) })),
      }],
    };
  }

  if (type === "liquidfill") {
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const val = rows.length ? num(rows[0][valueKey]) : 0;
    const maxRef = options.liquidMax || (val > 1 ? Math.max(...rows.map((r) => num(r[valueKey])), 100) : 1);
    let ratio = options.liquidMax ? val / options.liquidMax : (val > 1 ? val / 100 : val);
    if (!isFinite(ratio)) ratio = 0;
    ratio = Math.max(0, Math.min(1, ratio));
    return {
      graphic,
      series: [{
        type: "liquidFill", radius: "82%", center: ["50%", "50%"],
        data: [ratio, ratio * 0.92, ratio * 0.85],
        color: [colors[0], colors[1] || colors[0], colors[2] || colors[0]],
        backgroundStyle: { color: "rgba(43,78,255,0.06)", borderColor: colors[0], borderWidth: 1 },
        outline: { show: true, borderDistance: 4, itemStyle: { borderColor: colors[0], borderWidth: 2 } },
        label: { fontFamily: DISPLAY_FONT, fontSize: 30, color: INK(), formatter: () => `${(ratio * 100).toFixed(0)}%` },
      }],
    };
  }

  if (type === "pictorial_bar") {
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const cats = rows.map((r) => String(r[xKey]));
    const sym = imageSymbol(options) || "circle";
    return {
      ...common, legend: { ...baseLegend, show: false },
      xAxis: { type: "category", data: cats, axisLabel: { ...AXIS_LABEL, rotate: cats.length > 8 ? 30 : 0 }, axisLine: AXIS_LINE, axisTick: { show: false } },
      yAxis: { type: "value", axisLabel: AXIS_LABEL, splitLine: options.showGrid ? SPLIT_LINE : { show: false } },
      series: [{
        type: "pictorialBar",
        symbol: sym, symbolRepeat: true, symbolClip: true,
        symbolSize: [options.imageSize || 22, options.imageSize || 22],
        symbolBoundingData: Math.max(...rows.map((r) => num(r[valueKey])), 1),
        itemStyle: { color: colors[0] },
        label: valueLabel(options.showLabels),
        data: rows.map((r, i) => ({ value: num(r[valueKey]), itemStyle: { color: colors[i % colors.length] } })),
      }],
    };
  }

  if (type === "bar_3d") {
    // x = xKey category, y = seriesKey/2nd dim category, z = measure
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const yDim = seriesKey || (data.columns || []).find((c) => c !== xKey && c !== valueKey) || xKey;
    const xs = [...new Set(rows.map((r) => String(r[xKey])))];
    const ys = [...new Set(rows.map((r) => String(r[yDim])))];
    const d3 = rows.map((r) => [xs.indexOf(String(r[xKey])), ys.indexOf(String(r[yDim])), num(r[valueKey])]);
    const maxV = Math.max(...d3.map((d) => d[2]), 1);
    return {
      tooltip: { ...baseTooltip },
      visualMap: { max: maxV, min: 0, dimension: 2, inRange: { color: SEQUENTIAL_OCEAN.concat([colors[0]]) }, calculable: true, left: 8, bottom: 8, textStyle: { color: INK2() } },
      xAxis3D: { type: "category", data: xs, axisLabel: { color: INK2() }, name: "" },
      yAxis3D: { type: "category", data: ys, axisLabel: { color: INK2() }, name: "" },
      zAxis3D: { type: "value", axisLabel: { color: INK2() } },
      grid3D: {
        boxWidth: 100, boxDepth: 80, viewControl: { autoRotate: !!options.autoRotate, autoRotateSpeed: 8, distance: 200 },
        light: { main: { intensity: 1.2, shadow: true }, ambient: { intensity: 0.35 } },
        environment: "auto",
      },
      series: [{ type: "bar3D", data: d3, shading: "lambert", itemStyle: { opacity: 0.95 }, emphasis: { label: { show: false } } }],
    };
  }

  if (type === "scatter_3d") {
    // 3 measures => x,y,z
    const xk = yKeys[0] || data.columns[1];
    const yk = yKeys[1] || data.columns[2] || xk;
    const zk = yKeys[2] || data.columns[3] || yk;
    const d3 = rows.map((r) => [num(r[xk]), num(r[yk]), num(r[zk]), String(r[xKey])]);
    const maxV = Math.max(...d3.map((d) => d[2]), 1);
    return {
      tooltip: { ...baseTooltip, formatter: (p) => `${p.value[3]}<br/>${xk}: ${formatNumber(p.value[0])}<br/>${yk}: ${formatNumber(p.value[1])}<br/>${zk}: ${formatNumber(p.value[2])}` },
      visualMap: { max: maxV, min: 0, dimension: 2, inRange: { color: SEQUENTIAL_OCEAN.concat([colors[0]]) }, calculable: true, left: 8, bottom: 8, textStyle: { color: INK2() } },
      xAxis3D: { type: "value", name: xk, axisLabel: { color: INK2() } },
      yAxis3D: { type: "value", name: yk, axisLabel: { color: INK2() } },
      zAxis3D: { type: "value", name: zk, axisLabel: { color: INK2() } },
      grid3D: {
        viewControl: { autoRotate: !!options.autoRotate, autoRotateSpeed: 8, distance: 220 },
        light: { main: { intensity: 1.1 }, ambient: { intensity: 0.4 } },
      },
      series: [{ type: "scatter3D", data: d3, symbolSize: 12, itemStyle: { opacity: 0.85, borderWidth: 0 } }],
    };
  }

  if (type === "heatmap") {
    // Items 49/50: axes (swap/sort/titles/top-down), legend (position/type/steps/format), labels (mode/size/auto-contrast),
    // semantic tooltip with shares, explicit empty cells, cell radius/borders. Item 131 gradient stops + scale bounds kept.
    const yField0 = seriesKey || data.columns[1];
    const valueKey = yKeys[0] || data.columns[2];
    const fmtV = (v) => fmtVal(v, valueKey);
    const swap = !!options.heatSwapAxes;
    const xF = swap ? yField0 : xKey, yF = swap ? xKey : yField0;
    const cellMap = new Map();
    const rowTot = {}, colTot = {};
    let grand = 0;
    rows.forEach((r) => {
      const x = String(r[xF]), y = String(r[yF]), v = num(r[valueKey]);
      const k = `${x}\u0001${y}`;
      cellMap.set(k, (cellMap.get(k) || 0) + v);
      colTot[x] = (colTot[x] || 0) + v; rowTot[y] = (rowTot[y] || 0) + v; grand += v;
    });
    const xs = sortCats([...new Set(rows.map((r) => String(r[xF])))], options.heatSortX, colTot);
    const ys = sortCats([...new Set(rows.map((r) => String(r[yF])))], options.heatSortY, rowTot);
    const vals = [...cellMap.values()];
    const dataMax = vals.length ? Math.max(...vals) : 1;
    const dataMin = vals.length ? Math.min(...vals) : 0;
    const vmMin = hasV(options, "heatMin") ? Number(options.heatMin) : (dataMin === dataMax ? Math.min(0, dataMin) : dataMin);
    const vmMax = hasV(options, "heatMax") ? Number(options.heatMax) : (dataMax || 1);
    const stops = [options.heatLow || SEQ_LOW(), options.heatMid || colors[0], options.heatHigh || SEQ_HIGH()];
    const fmtC = options.heatLabelCompact === false ? fmtV : fmtOverride(null, valueKey, true);
    const colorAt = (v) => lerpStops(stops, (v - vmMin) / ((vmMax - vmMin) || 1));
    const dense = Math.max(xs.length, ys.length);
    const lblMode = options.heatLabelMode || (options.showLabels ? "value" : "none");
    const autoSize = dense > 40 ? 0 : dense > 24 ? 8 : dense > 14 ? 9 : dense > 8 ? 10 : 11;
    const lblSize = hasV(options, "heatLabelSize") ? Number(options.heatLabelSize) : autoSize;
    const showLbl = lblMode !== "none" && lblSize > 0;
    const pctDec = Number.isInteger(options.heatPctDecimals) ? options.heatPctDecimals : 1;
    const pct = (v, base) => (base ? `${((v / base) * 100).toFixed(pctDec)}%` : "");
    const lblText = (x, y, v) => {
      if (lblMode === "percent_total") return pct(v, grand);
      if (lblMode === "percent_row") return pct(v, rowTot[y]);
      if (lblMode === "percent_col") return pct(v, colTot[x]);
      if (lblMode === "value_percent") return `${fmtC(v)} (${pct(v, grand)})`;
      return fmtC(v);
    };
    const lblColorMode = options.heatLabelColor || "auto";
    const hd = [], empty = [];
    xs.forEach((x, xi) => ys.forEach((y, yi) => {
      const k = `${x}\u0001${y}`;
      if (cellMap.has(k)) {
        const v = cellMap.get(k);
        hd.push({ name: x, value: [xi, yi, v], label: { color: lblColorMode === "auto" ? contrastInk(colorAt(v)) : lblColorMode } });
      } else if (options.heatShowEmpty !== false) empty.push({ name: x, value: [xi, yi, 0] });
    }));
    const cellStyle = { borderColor: options.heatBorderColor || CARD_BG(), borderWidth: Number(options.heatBorderWidth ?? 1), borderRadius: hasV(options, "heatCellRadius") ? Number(options.heatCellRadius) : 4 };
    const legendPos = options.heatLegendPos || "bottom";
    const legendType = options.heatLegendType === "piecewise" ? "piecewise" : "continuous";
    const steps = Math.max(2, Math.min(12, Number(options.heatSteps) || 5));
    const vertical = legendPos === "left" || legendPos === "right";
    const vm = {
      type: legendType, min: vmMin, max: vmMax, seriesIndex: [0], show: legendPos !== "hidden",
      calculable: legendType === "continuous", inRange: { color: stops }, textStyle: { color: INK2(), fontSize: 10, fontFamily: CHART_FONT },
      formatter: (a, b) => (legendType === "piecewise" && b !== undefined ? (vertical ? `${fmtC(a)} \u2013 ${fmtC(b)}` : `\u2264 ${fmtC(b)}`) : fmtC(a)),
      ...(legendType === "piecewise" ? { splitNumber: steps, itemWidth: 14, itemHeight: 10, itemGap: 6 } : { itemWidth: 12, itemHeight: vertical ? 120 : 140 }),
      ...(options.heatLegendTitle ? { text: [options.heatLegendTitle], textGap: 8 } : {}),
    };
    const grid = { ...baseGrid, bottom: 36 };
    const legW = 14 + 10 + estW(legendType === "piecewise" ? `${fmtC(vmMax)} \u2013 ${fmtC(vmMax)}` : fmtC(vmMax));
    if (legendPos === "bottom") { Object.assign(vm, { orient: "horizontal", left: "center", bottom: 0 }); grid.bottom = legendType === "piecewise" ? 64 : 58; }
    else if (legendPos === "top") { Object.assign(vm, { orient: "horizontal", left: "center", top: 0 }); grid.top = legendType === "piecewise" ? 60 : 52; }
    else if (legendPos === "right") { Object.assign(vm, { orient: "vertical", right: 0, top: "middle" }); grid.right = legW + 12; }
    else if (legendPos === "left") { Object.assign(vm, { orient: "vertical", left: 0, top: "middle" }); grid.left = legW + 12; }
    const autoT = !!options.axisAutoTitles;
    const xTitle = options.xTitle || (autoT ? labelOf(xF) : "");
    const yTitle = options.yTitle || (autoT ? labelOf(yF) : "");
    const xRot = catAxisRotation(options, xs.length);
    const xLabelW = Math.max(...xs.map(estW), 0);
    const yLabelW = Math.min(150, Math.max(...ys.map(estW), 0));
    const xRotH = xRot ? Math.round(Math.min(120, xLabelW * Math.sin((Math.abs(xRot) * Math.PI) / 180))) : 0;
    if (xRotH) grid.bottom += xRotH;            // item 14: rotated category labels need their own room
    if (xTitle) grid.bottom += 14;
    if (yTitle) grid.left = (grid.left || 8) + Math.max(14, Math.round((yLabelW + 14) - yLabelW * 0.75 + 8));
    const xAxis = applyAxisFmt({
      type: "category", data: xs, name: xTitle, nameLocation: "middle", nameGap: xRot ? Math.min(140, xRotH + 24) : 30, nameTextStyle: { color: INK3(), fontFamily: CHART_FONT, fontSize: 11 },
      axisLabel: { ...AXIS_LABEL, rotate: xRot, hideOverlap: true, interval: xs.length > 60 ? "auto" : 0 }, axisTick: { show: false }, splitArea: { show: options.heatShowEmpty === false },
    }, (options.axisFmt || {}).x);
    const yAxis = applyAxisFmt({
      type: "category", data: ys, inverse: !!options.heatYTopDown, name: yTitle, nameLocation: "middle", nameGap: yLabelW + 14, nameTextStyle: { color: INK3(), fontFamily: CHART_FONT, fontSize: 11 },
      axisLabel: { ...AXIS_LABEL, width: 150, overflow: "truncate", hideOverlap: true, interval: ys.length > 60 ? "auto" : 0 }, axisTick: { show: false }, splitArea: { show: options.heatShowEmpty === false },
    }, (options.axisFmt || {}).y);
    const ttFor = (x, y, v, isEmpty) => {
      const chip = isEmpty ? "" : `<span style="display:inline-block;width:10px;height:10px;border-radius:3px;background:${colorAt(v)};margin-right:6px"></span>`;
      const lines = [`<div style="font-weight:600;margin-bottom:3px">${chip}${escH(labelOf(xF))}: ${escH(x)}</div>`, `<div>${escH(labelOf(yF))}: <b>${escH(y)}</b></div>`];
      if (isEmpty) lines.push(`<div style="opacity:.7">${escH(labelOf(valueKey))}: ${escH(options.heatEmptyText || "no data")}</div>`);
      else {
        lines.push(`<div>${escH(labelOf(valueKey))}: <b>${escH(fmtV(v))}</b></div>`);
        if (options.heatTooltipShares !== false && grand) lines.push(`<div style="opacity:.75;font-size:11px;margin-top:2px">${pct(v, grand)} of total \u00B7 ${pct(v, rowTot[y])} of ${escH(y)} \u00B7 ${pct(v, colTot[x])} of ${escH(x)}</div>`);
      }
      return lines.join("");
    };
    const series = [{
      type: "heatmap", data: hd, name: labelOf(valueKey),
      label: { show: showLbl, fontSize: lblSize || 10, fontFamily: CHART_FONT, ...(options.heatLabelBold ? { fontWeight: 600 } : {}), formatter: (p) => lblText(xs[p.value[0]], ys[p.value[1]], p.value[2]) },
      itemStyle: cellStyle, labelLayout: { hideOverlap: true },
      emphasis: { itemStyle: { shadowBlur: 10, shadowColor: "rgba(10,16,41,0.35)", borderColor: INK(), borderWidth: 1.5 } },
    }];
    if (empty.length) {
      series.push({
        type: "heatmap", data: empty, silent: true, z: 1,
        itemStyle: { ...cellStyle, color: options.heatNullColor || (isDark() ? "rgba(255,255,255,0.04)" : "rgba(10,16,41,0.04)") },
        label: { show: showLbl && !!options.heatEmptyText, color: INK3(), fontSize: lblSize || 10, formatter: () => options.heatEmptyText || "" },
        emphasis: { disabled: true },
      });
    }
    return {
      tooltip: { ...baseTooltip, position: "top", confine: true, formatter: (p) => ttFor(xs[p.value[0]], ys[p.value[1]], p.value[2], p.seriesIndex === 1) },
      graphic, grid, xAxis, yAxis, visualMap: vm, series,
    };
  }

  if (type === "gauge") {
    // Items 135-136: target, threshold bands, needle, arc width, current/target label toggles
    const valueKey = yKeys[0] || data.columns[data.columns.length - 1];
    const val = rows.length ? num(rows[0][valueKey]) : 0;
    const targetFromMeasure = yKeys[1] != null && rows.length ? num(rows[0][yKeys[1]]) : null;
    const target = hasV(options, "gaugeTarget") ? Number(options.gaugeTarget) : targetFromMeasure;
    const min = Number(options.gaugeMin) || 0;
    // Item 16: an auto scale rounds up to a readable boundary so ticks are human numbers
    const niceUp = (v) => {
      if (!(v > 0)) return 100;
      const mag = Math.pow(10, Math.floor(Math.log10(v)));
      const n = v / mag;
      const step = n <= 1 ? 1 : n <= 1.2 ? 1.2 : n <= 1.5 ? 1.5 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 3 ? 3 : n <= 4 ? 4 : n <= 5 ? 5 : n <= 6 ? 6 : n <= 8 ? 8 : 10;
      return step * mag;
    };
    const autoMax = niceUp(Math.max(val * 1.25, target ? Number(target) * 1.1 : 0, min + 10));
    const maxRaw = hasV(options, "max") ? Number(options.max) : autoMax;
    const max = maxRaw > min ? maxRaw : min + Math.abs(min || 1);
    const width = Number(options.gaugeWidth) || 16;
    const bands = (options.gaugeBands || []).filter((b) => hasV(b, "to") && b.color).sort((a, b) => Number(a.to) - Number(b.to));
    // Items 53-54: scale ticks/labels with compact formatting, independent value / target / scale formats, target modes
    const GAUGE_ANGLES = { semi: [180, 0], full: [90, -269.999], three_quarter: [200, -20] };
    const [gStart, gEnd] = GAUGE_ANGLES[options.gaugeKind] || GAUGE_ANGLES.three_quarter;
    const fmtValue = fmtOverride(options.gaugeValueFormat, valueKey);
    const fmtTarget = fmtOverride(options.gaugeTargetFormat, yKeys[1] || valueKey);
    const fmtScale = fmtOverride(options.gaugeScaleFormat, valueKey, true);
    const showScale = !!options.gaugeShowScale;
    const splits = Math.max(2, Math.min(20, Number(options.gaugeSplits) || 5));
    const scaleColor = options.gaugeScaleColor || (showScale ? INK3() : "#94A3B8");
    let axisColor = [[1, "rgba(43,78,255,0.12)"]];
    if (bands.length) {
      axisColor = bands.map((b) => [Math.min(1, Math.max(0, (Number(b.to) - min) / ((max - min) || 1))), b.color]);
      if (axisColor[axisColor.length - 1][0] < 1) axisColor.push([1, "rgba(43,78,255,0.12)"]);
    }
    const bandColorFor = (v) => { for (const b of bands) { if (v <= Number(b.to)) return b.color; } return bands.length ? bands[bands.length - 1].color : colors[0]; };
    const valColor = bands.length ? bandColorFor(val) : colors[0];
    const showNeedle = options.showNeedle ?? bands.length > 0;
    const tMode = options.gaugeTargetMode || "label";
    const targetText = () => {
      const base = `${options.gaugeTargetText || "Target"}: ${fmtTarget(target)}`;
      if (tMode === "value_only") return fmtTarget(target);
      if (tMode === "delta") { const d = val - target; return `${base}  (${d >= 0 ? "+" : "\u2212"}${fmtValue(Math.abs(d))})`; }
      if (tMode === "pct") { const p = target ? (val / target) * 100 : null; return `${base}  (${p != null ? p.toFixed(Number.isInteger(options.gaugeTargetPctDecimals) ? options.gaugeTargetPctDecimals : 0) + "% of target" : "\u2014"})`; }
      return base;
    };
    const series = [{
      type: "gauge", startAngle: gStart, endAngle: gEnd, min, max, splitNumber: splits,
      progress: { show: !bands.length, width, itemStyle: { color: grad(valColor) } },
      axisLine: { lineStyle: { width, color: axisColor } },
      axisTick: showScale ? { show: true, splitNumber: 5, distance: 4, length: 5, lineStyle: { color: scaleColor, width: 1 } } : { show: false },
      splitLine: showScale ? { show: true, distance: 4, length: 10, lineStyle: { color: scaleColor, width: 2 } } : { show: false },
      axisLabel: options.gaugeScaleLabels === false
        ? { show: false }
        : { show: true, color: scaleColor, distance: showScale ? 28 : 22, fontSize: Number(options.gaugeScaleSize) || 10, fontFamily: CHART_FONT, formatter: (v) => fmtScale(v) },
      pointer: showNeedle ? { show: true, length: "58%", width: Number(options.pointerWidth) || 5, itemStyle: { color: options.pointerColor || INK() } } : { show: false },
      anchor: showNeedle ? { show: true, size: 10, itemStyle: { color: options.pointerColor || INK() } } : { show: false },
      detail: { show: options.showCurrentLabel !== false, valueAnimation: true, fontSize: Number(options.gaugeValueSize) || (showNeedle ? 22 : 28), fontFamily: DISPLAY_FONT, color: options.gaugeValueColor || (bands.length ? valColor : INK()), offsetCenter: [0, showNeedle ? "62%" : "0%"], formatter: (v) => fmtValue(v) },
      title: options.gaugeValueTitle ? { show: true, offsetCenter: [0, showNeedle ? "42%" : "24%"], color: INK3(), fontSize: Number(options.gaugeValueTitleSize) || 11, fontFamily: CHART_FONT } : { show: false },
      data: [{ value: Math.round(val * 100) / 100, name: options.gaugeValueTitle || "" }],
    }];
    if (target !== null && target !== undefined && isFinite(target)) {
      series.push({
        type: "gauge", startAngle: gStart, endAngle: gEnd, min, max, z: 3,
        progress: { show: false },
        axisLine: { lineStyle: { width: 0, color: [[1, "transparent"]] } },
        axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
        pointer: { show: true, icon: "rect", length: "78%", width: 3, itemStyle: { color: options.gaugeTargetColor || "#FF6B35" } },
        anchor: { show: false },
        detail: { show: options.showTargetLabel !== false, fontSize: Number(options.gaugeTargetSize) || 11, fontFamily: FONT, color: options.gaugeTargetLabelColor || INK3(), offsetCenter: [0, options.gaugeTargetOffset || "86%"], formatter: () => targetText() },
        data: [{ value: Math.min(max, Math.max(min, target)) }],
      });
    }
    return { graphic, series };
  }

  // Cartesian: bar, bar_stacked, line, area, combo (items 99-115: orientation, axis min/max,
  // display units, label modes, pos/neg colors, gradient config, bar sizing, markers, dash styles)
  const isLineLike = type === "line" || type === "area";
  const isBarType = type === "bar" || type === "bar_stacked";
  const horizontal = isBarType && options.orientation === "horizontal";
  const pn = options.posNeg || {};
  const pnThr = Number(pn.threshold ?? 0);
  const barRadiusOf = () => {
    const r = options.barRadius;
    if (r === undefined || r === null || r === "") return horizontal ? [4, 10, 10, 4] : [10, 10, 4, 4];
    const n = Number(r) || 0;
    return horizontal ? [0, n, n, 0] : [n, n, 0, 0];
  };
  const barFill = (base, value) => {
    let c = base;
    if (pn.enabled) c = Number(value) >= pnThr ? (pn.pos || "#16A34A") : (pn.neg || "#DC2626");
    else if (heatRange) c = heatLerp(Number(value));
    return options.gradient ? gradientFill(c, options, horizontal) : c;
  };
  // Item 33: continuous value-heat coloring for bars (low → high color ramp)
  const heat = options.barHeat || {};
  let heatRange = null;
  if (heat.enabled && isBarType) {
    const all = rows.flatMap((r) => yKeys.map((k) => num(r[k]))).filter(Number.isFinite);
    if (all.length && Math.max(...all) > Math.min(...all)) heatRange = [Math.min(...all), Math.max(...all)];
  }
  const heatLerp = (v) => {
    const hx = (h) => { const s = String(h || "").replace("#", ""); const f = s.length === 3 ? s.split("").map((c) => c + c).join("") : s; return [parseInt(f.slice(0, 2), 16) || 0, parseInt(f.slice(2, 4), 16) || 0, parseInt(f.slice(4, 6), 16) || 0]; };
    const a = hx(heat.low || "#BFDBFE"), b = hx(heat.high || "#1D4ED8");
    const tt = Math.max(0, Math.min(1, (v - heatRange[0]) / (heatRange[1] - heatRange[0] || 1)));
    return `rgb(${a.map((x, i) => Math.round(x + (b[i] - x) * tt)).join(",")})`;
  };
  const valOfP = (p) => (Array.isArray(p.value) ? p.value[p.value.length - 1] : p.value);
  const areaStyleOf = (c) => {
    if (type !== "area") return undefined;
    const op = options.areaOpacity ?? 0.2;
    if (options.areaGradient) {
      return { opacity: 1, color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: hexA(c, Math.max(op, 0.35)) }, { offset: 1, color: hexA(c, 0.02) }] } };
    }
    return { opacity: op };
  };
  const lineWidth = Number(options.lineWidth) || 2.5;
  const lineType = options.lineDash || "solid";
  const barSize = {
    ...(Number(options.barWidth) > 0 ? { barWidth: `${Number(options.barWidth)}%` } : {}),
    ...(options.barCatGap !== undefined && options.barCatGap !== null && options.barCatGap !== "" ? { barCategoryGap: `${Number(options.barCatGap)}%` } : {}),
  };

  let categories, series;
  if (seriesKey) {
    categories = [...new Set(rows.map((r) => String(r[xKey])))];
    const seriesNames = [...new Set(rows.map((r) => String(r[seriesKey])))];
    const mKey = yKeys[0];
    const mk = markerConfig(options, false);
    const nullMode = options.nullMode || "gap";
    const lineVal = (v) => (nullMode === "zero" ? num(v) : numOrNull(v));
    // Item 7: a single global bar colour only applies when there is one bar series to colour
    const globalBar = !isLineLike && seriesNames.length === 1 ? options.barColor : null;
    series = seriesNames.map((sn, idx) => {
      const base = seriesColorOf(options, sn, idx, colors, globalBar);
      return {
        name: sn, type: isLineLike ? "line" : "bar",
        stack: type === "bar_stacked" || type === "area" ? "total" : undefined,
        smooth: isLineLike ? options.smooth : false,
        symbol: isLineLike ? mk.symbol : undefined,
        symbolSize: isLineLike ? mk.symbolSize : undefined,
        areaStyle: areaStyleOf(base),
        lineStyle: { width: lineWidth, type: lineType, cap: "round", color: isLineLike ? base : undefined },
        ...(isLineLike ? {} : barSize),
        itemStyle: isLineLike
          ? { color: options.markerColor || base }
          : { borderRadius: barRadiusOf(), color: (pn.enabled || heatRange) ? (p) => barFill(base, valOfP(p)) : barFill(base) },
        data: categories.map((c) => {
          const row = rows.find((r) => String(r[xKey]) === c && String(r[seriesKey]) === sn);
          if (!row) return isLineLike && nullMode !== "zero" ? null : 0;
          return isLineLike ? lineVal(row[mKey]) : num(row[mKey]);
        }),
        ...(isLineLike ? { connectNulls: nullMode === "connect" } : {}),
        __mkey: mKey,
      };
    });
  } else {
    categories = rows.map((r) => String(r[xKey]));
    const single = yKeys.length === 1;
    const mk = markerConfig(options, true);
    series = yKeys.map((yk, idx) => {
      // Item 87: combo — per-series chart type family, axis, markers, opacity via options.comboSeries
      const comboLines = type === "combo" ? Math.max(1, Math.min(yKeys.length - 1 || 1, Number(options.comboLines) || 1)) : 0;
      const csCfg = type === "combo" ? ((options.comboSeries || {})[yk] || {}) : {};
      const rawT = type === "combo"
        ? (csCfg.type || (idx >= yKeys.length - comboLines ? "line" : "bar"))
        : (isLineLike ? "line" : "bar");
      const LINE_FAM = ["line", "spline", "step", "area", "spline_area"];
      const SCAT_FAM = ["scatter", "dot", "bubble"];
      const isLolli = rawT === "lollipop";
      const isRange = rawT === "range_column" && type === "combo" && csCfg.rangeLow && csCfg.rangeLow !== yk && yKeys.includes(csCfg.rangeLow);
      const seriesType = LINE_FAM.includes(rawT) ? "line" : SCAT_FAM.includes(rawT) ? "scatter" : "bar";
      const isL = seriesType === "line";
      const comboArea = rawT === "area" || rawT === "spline_area";
      const base = (type === "combo" && csCfg.color) || seriesColorOf(options, yk, idx, colors, seriesType === "bar" && yKeys.length === 1 ? options.barColor : null);
      const onY2 = type === "combo" && yKeys.length > 1 && (csCfg.axis === "y2" || (!csCfg.axis && options.secondaryAxis && idx === yKeys.length - 1));
      const colorByData = single && options.colorByCategory && seriesType === "bar" && !pn.enabled;
      const vMaxAbs = rawT === "bubble" ? Math.max(...rows.map((r) => Math.abs(num(r[yk]))), 1) : 1;
      const sOpacity = csCfg.opacity != null && csCfg.opacity !== "" ? Number(csCfg.opacity) : undefined;
      const catColor = (p) => {
        const ov = (options.seriesColors || []).find((s) => s.name && String(s.name) === String(p.name) && s.color);
        return ov ? (options.gradient ? gradientFill(ov.color, options, horizontal) : ov.color) : undefined;
      };
      return {
        name: yk, type: seriesType,
        stack: type === "bar_stacked" ? "total" : undefined,
        smooth: isL ? (rawT === "spline" || rawT === "spline_area" ? true : (csCfg.smooth ?? options.smooth)) : false,
        ...(rawT === "step" ? { step: "middle" } : {}),
        symbol: isL
          ? (csCfg.marker === "none" ? "none" : (csCfg.marker || mk.symbol))
          : (seriesType === "scatter" ? (csCfg.marker || "circle") : undefined),
        symbolSize: seriesType === "scatter"
          ? (rawT === "bubble" ? ((val) => 6 + 22 * Math.abs(Number(val) || 0) / vMaxAbs) : (Number(csCfg.markerSize) || (rawT === "dot" ? 7 : 10)))
          : (isL ? (Number(csCfg.markerSize) || mk.symbolSize) : undefined),
        colorBy: colorByData ? "data" : "series",
        areaStyle: type === "combo"
          ? (comboArea ? { color: base, opacity: csCfg.areaOpacity != null && csCfg.areaOpacity !== "" ? Number(csCfg.areaOpacity) : 0.18 } : undefined)
          : areaStyleOf(base),
        lineStyle: { width: Number(csCfg.width) || lineWidth, type: csCfg.dash || lineType, cap: "round", color: isL ? base : undefined, ...(sOpacity != null ? { opacity: sOpacity } : {}) },
        ...(seriesType === "bar" ? (isLolli ? { barWidth: 3 } : barSize) : {}),
        ...(onY2 ? { yAxisIndex: 1 } : {}),
        itemStyle: isL || seriesType === "scatter"
          ? { color: (type === "combo" && csCfg.color) || options.markerColor || base, ...(sOpacity != null ? { opacity: sOpacity } : {}) }
          : {
            borderRadius: isLolli ? 2 : barRadiusOf(),
            ...(sOpacity != null ? { opacity: sOpacity } : {}),
            color: colorByData
              ? ((options.seriesColors || []).length ? catColor : undefined)
              : ((pn.enabled || heatRange) ? (p) => barFill(base, valOfP(p)) : barFill(base)),
          },
        data: isRange ? rows.map((r) => Math.max(0, num(r[yk]) - num(r[csCfg.rangeLow]))) : rows.map((r) => (isL ? ((options.nullMode || "gap") === "zero" ? num(r[yk]) : numOrNull(r[yk])) : num(r[yk]))),
        ...(isL ? { connectNulls: (options.nullMode || "gap") === "connect" } : {}),
        __mkey: yk,
        ...(isLolli ? { __lolli: true, __lolliSize: Number(csCfg.markerSize) || 9, __lolliColor: base } : {}),
        ...(isRange ? {
          stack: `__rng_${yk}`, __rangeLow: csCfg.rangeLow,
          tooltip: {
            valueFormatter: (val, di) => (Number.isInteger(di) && rows[di]
              ? `${fmtVal(num(rows[di][csCfg.rangeLow]), yk)} \u2013 ${fmtVal(num(rows[di][yk]), yk)}`
              : fmtVal(val, yk)),
          },
        } : {}),
      };
    });
  }

  // Item 87: lollipop combo series get a companion dot series (same name → shared legend entry)
  if (type === "combo" && series.some((s) => s.__lolli)) {
    const dots = series.filter((s) => s.__lolli).map((s) => ({
      name: s.name, type: "scatter", data: s.data, symbolSize: s.__lolliSize, z: 3,
      itemStyle: { color: s.__lolliColor },
      ...(s.yAxisIndex != null ? { yAxisIndex: s.yAxisIndex } : {}),
      __mkey: s.__mkey,
    }));
    series = [...series, ...dots];
  }

  // Item 87: range column — transparent stacked base so the visible bar spans low → high
  if (type === "combo" && series.some((s) => s.__rangeLow)) {
    series = series.flatMap((s) => {
      if (!s.__rangeLow) return [s];
      const lows = rows.map((r) => num(r[s.__rangeLow]));
      return [{
        name: `${s.name} (base)`, type: "bar", stack: s.stack, data: lows, silent: true,
        itemStyle: { color: "transparent" }, emphasis: { disabled: true }, tooltip: { show: false },
        ...(s.yAxisIndex != null ? { yAxisIndex: s.yAxisIndex } : {}),
        __rangeBase: true, __mkey: s.__mkey,
      }, s];
    });
  }

  // Item 106: infographic bars — decorate/replace plain bars with an icon or product image
  if (type === "bar" && options.barImageUrl && String(options.barImageUrl).trim()) {
    const boundMax = Math.max(...series.flatMap((s) => (s.data || []).map((d) => Number(d?.value ?? d) || 0)), 1);
    const stretch = options.barImageMode === "stretch";
    const iconSize = Number(options.barImageSize) || 24;
    series = series.map((s) => s.type !== "bar" ? s : ({
      name: s.name, type: "pictorialBar",
      symbol: "image://" + String(options.barImageUrl).trim(),
      symbolRepeat: !stretch, symbolClip: !stretch,
      symbolSize: stretch ? ["100%", "100%"] : [iconSize, iconSize],
      symbolBoundingData: boundMax,
      data: s.data, __mkey: s.__mkey,
    }));
  }

  // Data labels (items 102-103): content mode, display units, percent-of bases
  // Item 22: combo charts configure bar and line labels independently (show / position / content /
  // typography / rotation) and keep y2 series on the secondary-axis display unit.
  const comboLbl = type === "combo" ? (options.comboLabels || {}) : {};
  const comboLblOn = type === "combo" && (comboLbl.bar?.show || comboLbl.line?.show);
  if (options.showLabels || comboLblOn) {
    const catTotals = {};
    const seriesTotals = {};
    series.forEach((s) => { seriesTotals[s.name] = (s.data || []).reduce((a, d) => a + (Number(d?.value ?? d) || 0), 0); });
    categories.forEach((c, ci) => { catTotals[c] = series.reduce((a, s) => a + (Number(s.data?.[ci]?.value ?? s.data?.[ci]) || 0), 0); });
    const stacked = type === "bar_stacked";
    const pctBase = (p) => (stacked ? catTotals[p.name] : seriesTotals[p.seriesName]);
    const pos = options.labelPosition || (stacked ? "inside" : (horizontal ? "right" : "top"));
    const lblStyle = dataLabelStyle(options, pos === "inside" ? "#fff" : INK2(), 10, FONT);
    const hideOverlap = (options.dataLabel || {}).hideOverlap !== false;
    const unit2Fmt = unitFormatter(options.y2Unit, options.axisDecimals);
    const famOf = (s) => (s.type === "line" || s.type === "scatter" ? "line" : "bar");
    series = series.map((s) => {
      if (s.__rangeBase) return s;
      if (type !== "combo") {
        return {
          ...s,
          label: {
            show: true, position: pos, ...lblStyle,
            formatter: cartesianLabelFormatter({ mode: options.labelMode, fmtVal: (v) => fmtVal(v, s.__mkey), pctBase }),
          },
          // Item 7: never let crowded labels overlap each other
          labelLayout: { hideOverlap },
        };
      }
      const fam = famOf(s);
      const ov = comboLbl[fam] || {};
      const show = ov.show === undefined ? !!options.showLabels : !!ov.show;
      if (!show) return { ...s, label: { show: false } };
      const fPos = ov.position || options.labelPosition || (fam === "line" ? "top" : "top");
      const fStyle = dataLabelStyleFrom(
        { ...(options.dataLabel || {}), ...ov },
        fPos === "inside" ? "#fff" : INK2(), 10, FONT,
      );
      const onY2 = s.yAxisIndex === 1;
      const fmtOne = (v) => (onY2 && unit2Fmt ? unit2Fmt(v) : fmtVal(v, s.__mkey));
      const famHide = ov.hideOverlap === undefined ? hideOverlap : !!ov.hideOverlap;
      return {
        ...s,
        label: {
          show: true, position: fPos, ...fStyle,
          distance: ov.distance != null && ov.distance !== "" ? Number(ov.distance) : (fam === "line" ? 8 : 4),
          formatter: cartesianLabelFormatter({ mode: ov.mode || options.labelMode, fmtVal: fmtOne, pctBase }),
        },
        // Collision handling: shift labels away from each other first, hide only what still clashes
        labelLayout: { hideOverlap: famHide, ...(ov.moveOverlap === "off" ? {} : { moveOverlap: "shiftY" }) },
        ...(fam === "line" ? { z: (s.z || 2) + 2 } : {}),
      };
    });
  }
  series.forEach((s) => { if (s.name) { mkeyOf[s.name] = s.__mkey; if (s.__rangeLow) rangeOf[s.name] = s.__rangeLow; } });
  const rangeBaseNames = series.filter((s) => s.__rangeBase).map((s) => s.name);
  series = series.map(({ __mkey, __lolli, __lolliSize, __lolliColor, __rangeBase, __rangeLow, ...s }) => s);

  // Items 32/34: semantic axis titles (opt-in) + collision-aware gaps from estimated label widths
  const autoT = !!options.axisAutoTitles;
  const xTitle = options.xTitle || (autoT ? labelOf(xKey) : "");
  const yTitle = options.yTitle || (autoT && yKeys.length === 1 ? labelOf(yKeys[0]) : "");
  const estVals = series.flatMap((s) => (s.data || []).map((d) => Math.abs(Number(d?.value ?? d) || 0)));
  const valLabelW = estW(unitFmt ? unitFmt(Math.max(...estVals, 0)) : formatNumber(Math.max(...estVals, 0)));
  const catLabelW = Math.min(150, Math.max(...categories.map(estW), 0));
  const gridH = options.showGridH ?? options.showGrid;
  const gridV = options.showGridV ?? false;
  const catAxis = {
    type: "category", data: categories,
    inverse: options.invertCat ?? horizontal,
    name: xTitle, nameLocation: "middle", nameGap: horizontal ? Math.max(40, catLabelW + 16) : 34,
    nameTextStyle: { color: INK3(), fontFamily: FONT, fontSize: 11 },
    axisLabel: { ...AXIS_LABEL, hideOverlap: true, rotate: horizontal ? 0 : catAxisRotation(options, categories.length), ...(horizontal ? { width: 150, overflow: "truncate" } : {}) },
    axisLine: AXIS_LINE, axisTick: { show: false },
    splitLine: gridV ? SPLIT_LINE : { show: false },
  };
  const valAxis = {
    type: "value",
    inverse: !!options.invertVal,
    ...valueAxisBounds(options.yMin, options.yMax),
    ...(options.yInterval ? { interval: Number(options.yInterval) } : {}),
    name: yTitle, nameLocation: "middle", nameGap: horizontal ? 30 : Math.max(46, valLabelW + 16),
    nameTextStyle: { color: INK3(), fontFamily: FONT, fontSize: 11 },
    axisLabel: { ...AXIS_LABEL, hideOverlap: true, ...(unitFmt ? { formatter: unitFmt } : {}) },
    splitLine: gridH ? SPLIT_LINE : { show: false },
  };
  let yAxisOut = horizontal ? catAxis : valAxis;
  const wantsY2 = type === "combo" && yKeys.length > 1 && (options.secondaryAxis || Object.values(options.comboSeries || {}).some((c) => c && c.axis === "y2"));
  if (!horizontal && wantsY2) {
    const unit2 = unitFormatter(options.y2Unit, options.axisDecimals);
    const y2Keys = yKeys.filter((k, i) => { const c = (options.comboSeries || {})[k] || {}; return c.axis === "y2" || (!c.axis && options.secondaryAxis && i === yKeys.length - 1); });
    yAxisOut = [valAxis, {
      type: "value", ...valueAxisBounds(options.y2Min, options.y2Max),
      ...(options.y2Interval ? { interval: Number(options.y2Interval) } : {}),
      name: options.y2Title || (autoT && y2Keys.length === 1 ? labelOf(y2Keys[0]) : ""), nameLocation: "middle", nameGap: Math.max(46, valLabelW + 16),
      nameTextStyle: { color: INK3(), fontFamily: FONT, fontSize: 11 },
      axisLabel: { ...AXIS_LABEL, ...(unit2 ? { formatter: unit2 } : {}) },
      splitLine: { show: false },
    }];
  }
  // Item 34/36/38: independent per-axis formatting (X = bottom, Y = left, Y2 = right)
  const af = options.axisFmt || {};
  applyAxisFmt(horizontal ? valAxis : catAxis, af.x);
  applyAxisFmt(horizontal ? catAxis : valAxis, af.y);
  if (Array.isArray(yAxisOut) && yAxisOut[1]) applyAxisFmt(yAxisOut[1], af.y2);
  const xRot = Math.abs(Number((horizontal ? valAxis : catAxis).axisLabel?.rotate) || 0);
  const xRotH = !horizontal && xRot ? Math.round(Math.min(120, catLabelW * Math.sin((xRot * Math.PI) / 180))) : 0;
  if (xRotH) catAxis.nameGap = Math.max(catAxis.nameGap || 34, xRotH + 26);
  const opt = {
    ...common,
    xAxis: horizontal ? valAxis : catAxis,
    yAxis: yAxisOut,
    series,
  };
  if (rangeBaseNames.length && opt.legend) opt.legend = { ...opt.legend, data: series.filter((s) => !rangeBaseNames.includes(s.name)).map((s) => s.name) };
  const leftTitle = (horizontal ? catAxis : valAxis).name;
  const bottomTitle = (horizontal ? valAxis : catAxis).name;
  if (leftTitle) { const lw = horizontal ? catLabelW : valLabelW; const ng = (horizontal ? catAxis : valAxis).nameGap || 46; opt.grid = { ...opt.grid, left: (opt.grid.left || 8) + Math.max(16, Math.round(ng - lw * 0.75 + 8)) }; }
  if (bottomTitle) opt.grid = { ...opt.grid, bottom: (opt.grid.bottom || 36) + 10 + (xRotH ? 12 : 0) };
  if (Array.isArray(yAxisOut) && yAxisOut[1] && yAxisOut[1].name) opt.grid = { ...opt.grid, right: (opt.grid.right || 16) + 16 };
  // Item 7: bar / column / combo start scrolling automatically on dense category sets;
  // line & area keep every point unless the user turns zooming on explicitly.
  applyDataZoom(opt, options, horizontal, categories.length, isBarType || type === "combo");
  return opt;
}

// ---- Geographic maps ----
function mapFormatters(data, spec, optionsIn) {
  const fmtByKey = {};
  (((data && data.meta) || {}).measures || []).forEach((m) => { if (m.key) fmtByKey[m.key] = m.format; });
  const labelByKey = labelsFromMeta(data);
  const wloc = optionsIn.locale || undefined;
  const fmt = (v, key) => (fmtByKey[key] ? formatValue(v, fmtByKey[key], wloc) : formatNumber(v, wloc));
  const fmtCompact = (v, key) => { const b = fmtByKey[key]; const n = Number(v); const c = b && !b.scale && Math.abs(n) >= 1e4; return b ? formatValue(v, { ...b, scale: b.scale || (c ? "auto" : ""), ...(c ? { decimals: Math.max(Number.isInteger(b.decimals) ? b.decimals : 0, 1) } : {}) }, wloc) : formatNumber(v, wloc); };
  const labelOf = (k) => (optionsIn.seriesLabels || {})[k] || labelByKey[k] || k;
  const nameKey = spec.x_axis || (data.columns && data.columns[0]);
  const valKeys = (spec.y_axis && spec.y_axis.length ? spec.y_axis : [(data.columns || [])[1]]).filter(Boolean);
  return { fmt, fmtCompact, labelOf, nameKey, valKeys, valKey: valKeys[0] };
}

// Legend (visualMap) placement shared by choropleth + value-coloured bubble maps
function mapVisualMap(options, minV, maxV, stops, fmtV) {
  const pos = options.mapLegendPos || "bottom_left";
  const piece = options.mapLegendType === "piecewise";
  const horiz = pos === "bottom" || pos === "top";
  const vm = {
    type: piece ? "piecewise" : "continuous", min: minV, max: maxV, show: pos !== "hidden", calculable: !piece,
    inRange: { color: stops }, textStyle: { color: INK2(), fontSize: 10, fontFamily: CHART_FONT },
    formatter: (a, b) => (piece && b !== undefined ? (horiz ? `\u2264 ${fmtV(b)}` : `${fmtV(a)} \u2013 ${fmtV(b)}`) : fmtV(a)),
    ...(piece ? { splitNumber: Math.max(2, Math.min(10, Number(options.mapSteps) || 5)), itemWidth: 14, itemHeight: 10 } : { itemWidth: 12, itemHeight: 110 }),
    ...(options.mapLegendTitle ? { text: [options.mapLegendTitle], textGap: 8 } : {}),
  };
  if (pos === "bottom") Object.assign(vm, { orient: "horizontal", left: "center", bottom: 8 });
  else if (pos === "top") Object.assign(vm, { orient: "horizontal", left: "center", top: 8 });
  else if (pos === "right") Object.assign(vm, { orient: "vertical", right: 12, top: "middle" });
  else if (pos === "top_left") Object.assign(vm, { orient: "vertical", left: 16, top: 16 });
  else Object.assign(vm, { orient: "vertical", left: 16, bottom: 16 });
  return vm;
}

// Choropleth (map heatmap): dimension = location name, measure = value
// Item 45: formatted semantic tooltips (all measures), region labels, legend position/type, colour stops,
// no-data / border styling. `regionNames` (optional) lets the caller learn which values matched no region.
export function buildChoropleth(data, spec, optionsIn = {}, mapName = "world", regionNames = null) {
  syncChartTheme();
  const options = { ...DEFAULT_OPTIONS, ...optionsIn };
  const colors = getColors(options);
  const rows = (data && data.rows) || [];
  const { fmt, fmtCompact, labelOf, nameKey, valKeys, valKey } = mapFormatters(data, spec, options);
  // Territory matching: remap raw values -> map feature names, summing collisions.
  // Items 11/12: a manual territory map wins, then the map's own spelling is resolved
  // diacritic/case-insensitively ("Maharashtra" -> "Mahārāshtra") so real data joins.
  const tmap = options.territoryMap || {};
  const aggByName = new Map();
  rows.forEach((r) => {
    const raw = String(r[nameKey]);
    const name = tmap[raw] || resolveRegion(mapName, raw) || raw;
    const acc = aggByName.get(name) || {};
    valKeys.forEach((k) => { acc[k] = (acc[k] || 0) + num(r[k]); });
    aggByName.set(name, acc);
  });
  const known = regionNames ? new Set(regionNames) : null;
  const unmatched = known ? [...aggByName.keys()].filter((n) => !known.has(n)) : [];
  const mapData = [...aggByName.entries()].map(([name, acc]) => ({ name, value: acc[valKey], __all: acc }));
  const vals = mapData.map((d) => d.value);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals, 0);
  const stops = [options.mapLow || SEQ_LOW(), options.mapMid || colors[0], options.mapHigh || SEQ_HIGH()];
  const fmtV = (v) => fmt(v, valKey);
  const fmtL = (v) => fmtCompact(v, valKey);
  const lblMode = options.mapLabelMode || "name";
  const opt = {
    tooltip: {
      ...baseTooltip, trigger: "item", confine: true,
      formatter: (p) => {
        const acc = (p.data && (p.data.__all || p.data.__row)) || null;
        const head = `<div style="font-weight:600;margin-bottom:3px">${p.marker || ""}${escH(p.name)}</div>`;
        if (!acc) return `${head}<div style="opacity:.7">${escH(options.mapNoDataText || "no data")}</div>`;
        return head + valKeys.map((k) => `<div style="display:flex;gap:10px"><span>${escH(labelOf(k))}</span><span style="margin-left:auto;font-weight:600">${escH(fmt(acc[k], k))}</span></div>`).join("");
      },
    },
    visualMap: mapVisualMap(options, minV, maxV, stops, fmtL),
    series: [{
      type: "map", map: mapName, roam: true, zoom: Number(options.mapZoom) || 1, scaleLimit: { min: 1, max: 12 },
      label: options.mapShowLabels
        ? { show: true, color: options.mapLabelColor || INK(), fontSize: Number(options.mapLabelSize) || 10, fontFamily: CHART_FONT, formatter: (p) => (p.value == null || isNaN(p.value) ? (options.mapLabelAll && lblMode !== "value" ? p.name : "") : lblMode === "value" ? fmtL(p.value) : lblMode === "name_value" ? `${p.name}\n${fmtL(p.value)}` : p.name) }
        : { show: false },
      labelLayout: { hideOverlap: true },
      emphasis: { label: { show: !!options.mapShowLabels }, itemStyle: { areaColor: colors[2] || colors[0] } },
      select: { disabled: true },
      itemStyle: { borderColor: options.mapBorderColor || "rgba(255,255,255,0.8)", borderWidth: hasV(options, "mapBorderWidth") ? Number(options.mapBorderWidth) : 0.5, areaColor: options.mapNoDataColor || (isDark() ? "#1A2138" : "#E7EBF5") },
      data: mapData,
    }],
  };
  opt.__unmatched = unmatched;
  return opt;
}

// Fit the geo view to a set of [lng, lat] points relative to the map's own extent (centroids of its regions)
export function fitGeoView(points, centroids = {}) {
  const pts = points.map((p) => p.value).filter((v) => Array.isArray(v) && Number.isFinite(v[0]) && Number.isFinite(v[1]));
  if (!pts.length) return {};
  const bbox = (arr) => arr.reduce((b, [x, y]) => [Math.min(b[0], x), Math.min(b[1], y), Math.max(b[2], x), Math.max(b[3], y)], [Infinity, Infinity, -Infinity, -Infinity]);
  const pb = bbox(pts);
  const cvals = Object.values(centroids);
  const mb = cvals.length > 1 ? bbox(cvals) : [-180, -60, 180, 80];
  const spanX = Math.max(pb[2] - pb[0], 0.5), spanY = Math.max(pb[3] - pb[1], 0.5);
  const mapX = Math.max(mb[2] - mb[0], 1), mapY = Math.max(mb[3] - mb[1], 1);
  const zoom = Math.max(1, Math.min(12, 0.7 * Math.min(mapX / spanX, mapY / spanY)));
  return { center: [(pb[0] + pb[2]) / 2, (pb[1] + pb[3]) / 2], zoom: Math.round(zoom * 10) / 10 };
}

// Bubble map: location dimension -> centroid, sized by measure.
// Item 47: normalised sizes, colour modes (single / category / series-dim / value gradient), labels, formatted semantic
// tooltips. Item 45: `extra.points` (pre-resolved [{name, value:[lng,lat,v], row}]) + `extra.view` for city drill levels.
export function buildBubbleMap(data, spec, centroids = {}, optionsIn = {}, mapName = "world", extra = {}) {
  syncChartTheme();
  const options = { ...DEFAULT_OPTIONS, ...optionsIn };
  const colors = getColors(options);
  const rows = (data && data.rows) || [];
  const { fmt, fmtCompact, labelOf, nameKey, valKeys, valKey } = mapFormatters(data, spec, options);
  const seriesKey = spec.series || null;
  const sym = imageSymbol(options);
  const tmap = options.territoryMap || {};
  const lookup = extra.lookup || (() => null);
  let points = extra.points;
  const unmatched = [];
  if (!points) {
    points = rows.map((r) => {
      const raw = String(r[nameKey]);
      const name = tmap[raw] || resolveRegion(mapName, raw) || raw;
      const c = centroids[name] || lookup(name);
      if (!c) { unmatched.push(name); return null; }
      return { name, value: [c[0], c[1], num(r[valKey])], row: r };
    }).filter(Boolean);
  }
  const maxV = Math.max(...points.map((p) => Math.abs(p.value[2])), 0) || 1;
  const bMin = Number(options.bubbleMin) || 8, bMax = Number(options.bubbleMax) || 40;
  const sizeOf = (val) => bMin + (bMax - bMin) * Math.sqrt(Math.abs(val[2]) / maxV);
  const groups = seriesKey ? [...new Set(points.map((p) => String((p.row || {})[seriesKey])))] : [null];
  const colorMode = options.bubbleColorMode || (seriesKey ? "series" : "single");
  const stops = [options.mapLow || SEQ_LOW(), options.mapMid || colors[0], options.mapHigh || SEQ_HIGH()];
  const fmtV = (v) => fmt(v, valKey);
  const lblMode = options.mapLabelMode || "name";
  const opacity = typeof options.bubbleOpacity === "number" ? options.bubbleOpacity : (sym ? 1 : 0.72);
  const legendOn = options.legendMode === "show" || (options.legendMode !== "hide" && groups.length > 1 && options.showLegend !== false);
  const series = groups.map((g, gi) => ({
    name: g == null ? labelOf(valKey) : g, type: "scatter", coordinateSystem: "geo",
    symbol: sym || options.markerShape || "circle",
    symbolSize: sizeOf,
    colorBy: colorMode === "category" ? "data" : "series",
    itemStyle: {
      color: g != null ? seriesColorOf(options, g, gi, colors) : (colorMode === "single" ? (options.markerColor || colors[0]) : undefined),
      opacity: sym ? 1 : opacity, borderColor: options.bubbleBorderColor || CARD_BG(), borderWidth: hasV(options, "bubbleBorderWidth") ? Number(options.bubbleBorderWidth) : 1,
    },
    emphasis: { focus: groups.length > 1 ? "series" : "none", scale: 1.2, itemStyle: { opacity: 0.95 } },
    label: options.mapShowLabels
      ? { show: true, position: options.mapLabelPos || "right", color: options.mapLabelColor || INK2(), fontSize: Number(options.mapLabelSize) || 10, fontFamily: CHART_FONT, formatter: (p) => (lblMode === "value" ? fmtV(p.value[2]) : lblMode === "name_value" ? `${p.name} ${fmtV(p.value[2])}` : p.name) }
      : { show: false },
    labelLayout: { hideOverlap: true },
    data: points.filter((p) => g == null || String((p.row || {})[seriesKey]) === g).map((p) => ({ name: p.name, value: p.value, __row: p.row || null })),
  }));
  const view = extra.view || {};
  const opt = {
    color: colors,
    tooltip: {
      ...baseTooltip, trigger: "item", confine: true,
      formatter: (p) => {
        const row = (p.data && p.data.__row) || null;
        const head = `<div style="font-weight:600;margin-bottom:3px">${p.marker || ""}${escH(p.name)}</div>`;
        const grp = seriesKey && row ? `<div>${escH(labelOf(seriesKey))}: <b>${escH(String(row[seriesKey]))}</b></div>` : "";
        const lines = row
          ? valKeys.map((k) => `<div style="display:flex;gap:10px"><span>${escH(labelOf(k))}</span><span style="margin-left:auto;font-weight:600">${escH(fmt(row[k], k))}</span></div>`).join("")
          : `<div>${escH(labelOf(valKey))}: <b>${escH(fmtV(p.value?.[2]))}</b></div>`;
        return head + grp + lines;
      },
    },
    legend: { ...baseLegend, show: legendOn, formatter: (n) => labelOf(n) },
    ...(colorMode === "value" ? { visualMap: { ...mapVisualMap(options, Math.min(...points.map((p) => p.value[2]), 0), Math.max(...points.map((p) => p.value[2]), 1), stops, (v) => fmtCompact(v, valKey)), dimension: 2, seriesIndex: series.map((_, i) => i) } } : {}),
    geo: {
      map: mapName, roam: true, zoom: view.zoom || Number(options.mapZoom) || 1, ...(view.center ? { center: view.center } : {}), scaleLimit: { min: 1, max: 12 },
      itemStyle: { areaColor: options.mapNoDataColor || (isDark() ? "#1A2138" : "#EEF3F9"), borderColor: options.mapBorderColor || "rgba(255,255,255,0.85)", borderWidth: hasV(options, "mapBorderWidth") ? Number(options.mapBorderWidth) : 0.5 },
      emphasis: { itemStyle: { areaColor: isDark() ? "#222B4A" : "#E2ECF6" }, label: { show: false } },
      select: { disabled: true },
    },
    series,
  };
  opt.__unmatched = unmatched;
  return opt;
}
